import React, { useState, useEffect, useRef, useMemo, Fragment } from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  HiPlus,
  HiTrash,
  HiDocumentDuplicate,
  HiXMark,
  HiPencil,
  HiEllipsisVertical,
  HiArrowPath,
  HiChevronDown,
  HiChevronRight,
  HiEyeSlash,
} from "react-icons/hi2";
import { Menu, Transition } from "@headlessui/react";
import { Schema } from "firebase/ai";
import {
  ActivityType,
  ActivityVariable,
  GlobalActivityType,
  TrainingType,
} from "../../types/activity";
import {
  ActivityMeasurement,
  SessionTemplate,
  Session,
} from "../../types/post";
import { activityService } from "../../services/activityService";
import { sessionService } from "../../services/sessionService";
import { GlobalActivityService } from "../../services/globalActivityService";
import { asDisplayNameOrNull } from "../../utils/displayName";
import { trainingTypeService } from "../../services/trainingTypeService";
import {
  normalizeActivityVariables,
  getUnitsForVariable,
  getDefaultUnit,
} from "../../utils/unitUtils";
import { isTimeDurationVariable } from "../../utils/timeDurationUtils";
import TimeDurationInput from "../common/TimeDurationInput";
import {
  applyPreferenceToVariables,
  getActivityPrefKey,
} from "../../utils/activityVariablePreferences";
import CreateActivityTypeDialog from "./CreateActivityTypeDialog";
import ActivitySelector from "../common/ActivitySelector";
import SearchableSelect from "../common/SearchableSelect";
import AILogButton, { type AILogFunction } from "../AILogButton";
import {
  countActivityGroups,
  initialCollapsedGroups,
  VARIABLE_COLUMN_WIDTH_CLASS,
} from "./activityGroupCollapse";

interface MasterSessionDetailsProps {
  // Activity measurements (the core data)
  plannedMeasurements: ActivityMeasurement[];
  onPlannedMeasurementsChange: (measurements: ActivityMeasurement[]) => void;

  // Template handling
  initialTemplate?: (SessionTemplate & { id: string }) | null;
  onTemplateSelect?: (template: SessionTemplate | null) => void;
  /**
   * When true the component starts directly in "build" mode — the exercise
   * list is shown immediately (ready for Add Exercise) and the session-template
   * picker is offered below the Add Exercise control instead of gating behind a
   * "How do you want to start?" step. Used by the Plan/Assign Session flow.
   */
  startInBuildMode?: boolean;
  /**
   * When true, the inline "Or start from a session template" picker shown in
   * build mode is hidden. Used when the caller supplies its own way of
   * choosing between scratch / template (e.g. the Plan Session modal).
   */
  hideTemplatePicker?: boolean;

  // Loading and error states
  loading?: boolean;
  error?: string | null;
  onErrorChange?: (error: string | null) => void;

  // Existing session for editing
  existingSession?: ((Session | SessionTemplate) & { id: string }) | null;

  // Reset handler
  onReset?: () => void;

  // Rendering/behavior mode
  mode?: "planning" | "logging";
  readOnly?: boolean;
  allowActivitySelection?: boolean;
  /** When false, Add Activity renders as an inline button instead of a floating FAB */
  floatingActivityButton?: boolean;

  // Refresh key to force re-fetching of session templates
  refreshKey?: number;

  /**
   * Offset added to every index this component emits through the scalar
   * index-based callbacks (onVariableUpdate/onDuplicateSet/onRemoveSet/
   * onRemoveGroup/onAddSet). Used when the component renders a contiguous
   * slice of a larger measurement list so writes map to the full list.
   */
  indexOffset?: number;

  // Optional external handlers (used by logging pages that persist per-action)
  onVariableUpdate?: (
    measurementIndex: number,
    variableIndex: number,
    value: number | null,
    unit?: string,
  ) => void;
  onDuplicateSet?: (index: number) => void;
  onRemoveSet?: (index: number) => void;
  onRemoveGroup?: (indices: number[]) => void;
  onAddSet?: (
    activityId: string,
    insertAfterIndex?: number,
    globalCategoryID?: string,
  ) => void;
  onAddActivity?: (
    activityType: ActivityType,
    insertAfterIndex?: number,
  ) => void;
  onBulkAddActivities?: (
    activityTypes: ActivityType[],
    insertAfterIndex?: number,
  ) => void;

  /** When true, hides the floating AI dictation button (e.g. when the parent renders it in a header instead). */
  hideFloatingAIButton?: boolean;
  /** Called whenever the AI prompt / logFunctions change so a parent can render its own AILogButton. */
  onAIConfigReady?: (prompt: string, logFunctions: AILogFunction[]) => void;
  /**
   * When provided, the AI's applyChanges handler will call this with the
   * full new measurements list (after timeLogged is applied) INSTEAD of
   * `onPlannedMeasurementsChange`. Use this in pages that auto-persist
   * per-row (e.g. the live logging page) so AI changes end up in the
   * backend the same way manual edits do. The promise should reject if
   * persistence fails so the AI surfaces a failure message.
   */
  onAIApplyChanges?: (
    measurements: ActivityMeasurement[],
  ) => void | Promise<void>;
  /**
   * When true, the UI hidden-column selections are mirrored onto each
   * measurement's `variable.hidden` flag (via `onPlannedMeasurementsChange`)
   * so downstream previews (e.g. the training-log session summary) hide the
   * same columns the user hid here. Off by default to avoid changing the
   * persistence behavior of other session flows.
   */
  syncHiddenColumnsToVariables?: boolean;
  /**
   * Controlled collapse state for activity groups (group index within this
   * instance). When provided together with `onCollapsedGroupsChange` the
   * parent owns the state — used by the live session page, which renders one
   * instance per coach block and needs a single source of truth. When
   * omitted, the component keeps its own state, seeded collapsed-by-default.
   */
  collapsedGroups?: Set<number>;
  onCollapsedGroupsChange?: (next: Set<number>) => void;
}

const MasterSessionDetails: React.FC<MasterSessionDetailsProps> = ({
  plannedMeasurements,
  onPlannedMeasurementsChange,
  initialTemplate,
  onTemplateSelect,
  startInBuildMode = false,
  hideTemplatePicker = false,
  loading = false,
  error = null,
  onErrorChange,
  existingSession,
  onReset,
  mode = "planning",
  readOnly = false,
  allowActivitySelection = true,
  floatingActivityButton = true,
  refreshKey,
  indexOffset = 0,
  onVariableUpdate: onVariableUpdateProp,
  onDuplicateSet: onDuplicateSetProp,
  onRemoveSet: onRemoveSetProp,
  onRemoveGroup: onRemoveGroupProp,
  onAddSet: onAddSetProp,
  onAddActivity,
  onBulkAddActivities,
  hideFloatingAIButton = false,
  onAIConfigReady,
  onAIApplyChanges,
  syncHiddenColumnsToVariables = false,
  collapsedGroups: collapsedGroupsProp,
  onCollapsedGroupsChange,
}) => {
  const { currentUser, userData } = useAuth();
  const isLoggingMode = mode === "logging";

  // When this instance renders a slice of a larger session (e.g. one section
  // block of an ordered notes+activities session), the parent passes an
  // `indexOffset` so that every index this component emits is translated back
  // to a position in the full ActivityMeasurementList. All persistence in the
  // athlete app is index-based, so this keeps writes correct for subsets.
  const onVariableUpdate = onVariableUpdateProp
    ? (
        measurementIndex: number,
        variableIndex: number,
        value: number | null,
        unit?: string,
      ) =>
        onVariableUpdateProp(
          measurementIndex + indexOffset,
          variableIndex,
          value,
          unit,
        )
    : undefined;
  const onDuplicateSet = onDuplicateSetProp
    ? (index: number) => onDuplicateSetProp(index + indexOffset)
    : undefined;
  const onRemoveSet = onRemoveSetProp
    ? (index: number) => onRemoveSetProp(index + indexOffset)
    : undefined;
  const onRemoveGroup = onRemoveGroupProp
    ? (indices: number[]) =>
        onRemoveGroupProp(indices.map((i) => i + indexOffset))
    : undefined;
  const onAddSet = onAddSetProp
    ? (
        activityId: string,
        insertAfterIndex?: number,
        globalCategoryID?: string,
      ) =>
        onAddSetProp(
          activityId,
          insertAfterIndex !== undefined
            ? insertAfterIndex + indexOffset
            : undefined,
          globalCategoryID,
        )
    : undefined;

  // State for activity types and session types
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, GlobalActivityType[]>
  >({});
  const [sessionTemplates, setSessionTemplates] = useState<SessionTemplate[]>(
    [],
  );

  // Activity selection state
  const [selectedActivityTypeId, setSelectedActivityTypeId] =
    useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [lastSelectedActivityId, setLastSelectedActivityId] =
    useState<string>("");
  const insertActivityAfterRef = useRef<number | undefined>(undefined);
  const [activitySelectorOpenId, setActivitySelectorOpenId] = useState(0);

  const openInsertActivity = (afterIndex: number) => {
    insertActivityAfterRef.current = afterIndex;
    setActivitySelectorOpenId((n) => n + 1);
  };

  // Template selection state
  const [selectedTemplate, setSelectedTemplate] =
    useState<SessionTemplate | null>(initialTemplate || null);
  const [showTemplateSelection, setShowTemplateSelection] =
    useState<boolean>(false);
  const [hasSelectedTemplate, setHasSelectedTemplate] = useState<boolean>(
    !!initialTemplate || !!existingSession || (startInBuildMode && !isLoggingMode),
  );
  const [startedFromScratch, setStartedFromScratch] = useState<boolean>(
    startInBuildMode && !isLoggingMode && !existingSession && !initialTemplate,
  );
  const [showClearConfirm, setShowClearConfirm] = useState<boolean>(false);

  // Bulk selection state
  const [bulkSelectionMode, setBulkSelectionMode] = useState<boolean>(false);
  const [selectedActivityIds, setSelectedActivityIds] = useState<Set<string>>(
    new Set(),
  );

  // Recent activities state
  const [recentActivityIds, setRecentActivityIds] = useState<string[]>([]);

  // Create activity dialog state
  const [isCreateActivityDialogOpen, setIsCreateActivityDialogOpen] =
    useState<boolean>(false);

  // Loading state
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);

  // State for unit dropdown visibility
  const [activeUnitDropdown, setActiveUnitDropdown] = useState<string | null>(
    null,
  );
  // Anchor rect for the unit dropdown so it can be rendered via a portal
  // (position: fixed) and not be clipped by the scrollable parent.
  const [unitDropdownRect, setUnitDropdownRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // State for tracking which activity groups have their Actions column hidden
  const [hiddenActionGroups, setHiddenActionGroups] = useState<Set<number>>(
    new Set(),
  );

  // State for tracking hidden columns per activity group
  // Key: `${groupIndex}-${variableIndex}`, Value: boolean (true = hidden)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set());

  // State for tracking columns that are animating out (being hidden)
  const [hidingColumns, setHidingColumns] = useState<Set<string>>(new Set());

  // Seed hidden columns from the coach's persisted `variable.hidden` flags so the
  // athlete sees the same columns the coach hid in the Session Designer. Runs once
  // per loaded session/template; the athlete can still reveal columns manually.
  const hiddenSeedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const seedKey = existingSession?.id || initialTemplate?.id || "__new__";
    if (hiddenSeedKeyRef.current === seedKey) return;
    if (!plannedMeasurements || plannedMeasurements.length === 0) return;

    // Replicate the render-time grouping: consecutive measurements that share the
    // same ActivityTypeID (+ globalCategoryID) form a group, and the first
    // measurement of each group drives the displayed variable columns.
    const seeded = new Set<string>();
    let groupIndex = -1;
    let lastKey: string | null = null;
    plannedMeasurements.forEach((m) => {
      const key = m.globalCategoryID
        ? `${m.ActivityTypeID}_${m.globalCategoryID}`
        : m.ActivityTypeID;
      if (key !== lastKey) {
        groupIndex += 1;
        lastKey = key;
        m.variables.forEach((v, variableIndex) => {
          if (v.hidden) seeded.add(`${groupIndex}-${variableIndex}`);
        });
      }
    });

    hiddenSeedKeyRef.current = seedKey;
    if (seeded.size > 0) {
      setHiddenColumns((prev) => {
        const next = new Set(prev);
        seeded.forEach((key) => next.add(key));
        return next;
      });
    }
  }, [plannedMeasurements, existingSession?.id, initialTemplate?.id]);

  // Optionally mirror the UI hidden-column selections onto each measurement's
  // `variable.hidden` flag so consumers that render from the measurements (e.g.
  // the training-log session preview) hide exactly the columns hidden here.
  // Converges after one pass because it only fires when a flag actually differs.
  useEffect(() => {
    if (!syncHiddenColumnsToVariables) return;
    if (!plannedMeasurements || plannedMeasurements.length === 0) return;

    // Rebuild the render-time grouping (consecutive measurements sharing the
    // same ActivityTypeID/globalCategoryID) and resolve, per group, which
    // variable names should be hidden based on the `${groupIndex}-${varIndex}`
    // keys in `hiddenColumns`.
    const hiddenNamesByMeasurement = new Map<number, Set<string>>();
    let groupIndex = -1;
    let lastKey: string | null = null;
    let currentGroupIndices: number[] = [];
    let currentHiddenNames = new Set<string>();
    const flushGroup = () => {
      currentGroupIndices.forEach((mi) =>
        hiddenNamesByMeasurement.set(mi, currentHiddenNames),
      );
    };
    plannedMeasurements.forEach((m, i) => {
      const key = m.globalCategoryID
        ? `${m.ActivityTypeID}_${m.globalCategoryID}`
        : m.ActivityTypeID;
      if (key !== lastKey) {
        flushGroup();
        groupIndex += 1;
        lastKey = key;
        currentGroupIndices = [i];
        currentHiddenNames = new Set<string>();
        m.variables.forEach((v, variableIndex) => {
          if (hiddenColumns.has(`${groupIndex}-${variableIndex}`)) {
            currentHiddenNames.add(v.name);
          }
        });
      } else {
        currentGroupIndices.push(i);
      }
    });
    flushGroup();

    let changed = false;
    const next = plannedMeasurements.map((m, mi) => {
      const hiddenNames =
        hiddenNamesByMeasurement.get(mi) ?? new Set<string>();
      let mChanged = false;
      const variables = m.variables.map((v) => {
        const shouldHide = hiddenNames.has(v.name);
        if (!!v.hidden !== shouldHide) {
          mChanged = true;
          return { ...v, hidden: shouldHide };
        }
        return v;
      });
      if (mChanged) {
        changed = true;
        return { ...m, variables };
      }
      return m;
    });

    if (changed) {
      onPlannedMeasurementsChange(next);
    }
  }, [
    syncHiddenColumnsToVariables,
    hiddenColumns,
    plannedMeasurements,
    onPlannedMeasurementsChange,
  ]);

  // State for tracking which row is being deleted (for animation)
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  // State for tracking newly added rows (for animation)
  const [newlyAddedIndex, setNewlyAddedIndex] = useState<number | null>(null);

  // Collapsed activity groups. Controlled by the parent when
  // `collapsedGroups`/`onCollapsedGroupsChange` are passed; otherwise internal.
  // Both paths start with every group collapsed (plan 3e / decision 4): until
  // the internal state is seeded it is derived from the current list, and the
  // seed is frozen the first time the list is non-empty so groups added later
  // open expanded.
  const isCollapseControlled = collapsedGroupsProp !== undefined;
  const [internalCollapsedGroups, setInternalCollapsedGroups] = useState<
    Set<number> | null
  >(null);
  useEffect(() => {
    if (isCollapseControlled || internalCollapsedGroups !== null) return;
    if (plannedMeasurements.length === 0) return;
    setInternalCollapsedGroups(initialCollapsedGroups(plannedMeasurements));
  }, [isCollapseControlled, internalCollapsedGroups, plannedMeasurements]);
  const collapsedGroups: Set<number> =
    collapsedGroupsProp ??
    internalCollapsedGroups ??
    initialCollapsedGroups(plannedMeasurements);
  const setCollapsedGroups = (
    next: Set<number> | ((prev: Set<number>) => Set<number>),
  ) => {
    const resolved = typeof next === "function" ? next(collapsedGroups) : next;
    if (isCollapseControlled) {
      onCollapsedGroupsChange?.(resolved);
    } else {
      setInternalCollapsedGroups(resolved);
    }
  };

  // State for dropdown position
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  // Training types for resolving template session type names
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  useEffect(() => {
    trainingTypeService
      .fetchTrainingTypes()
      .then(setTrainingTypes)
      .catch((err) =>
        console.error("Error fetching training types:", err),
      );
  }, []);

  // Refs for dropdowns
  const dropdownRef = useRef<HTMLDivElement>(null);
  const sessionTypeDropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // Set error helper
  const setError = (errorMessage: string | null) => {
    if (onErrorChange) {
      onErrorChange(errorMessage);
    }
  };

  // Helper to get activity name from either user activities or global activities
  const getActivityName = (measurement: ActivityMeasurement): string => {
    // If data is still loading, show loading state
    if (isLoadingData) {
      return "Loading...";
    }

    const getRawActivityId = (activityTypeId: string): string => {
      if (activityTypeId.startsWith("global_")) {
        const parts = activityTypeId.split("_");
        if (parts.length >= 3) {
          return parts.slice(2).join("_");
        }
      }
      return activityTypeId;
    };

    const getGlobalCategoryIdFromActivityTypeId = (
      activityTypeId: string,
    ): string | undefined => {
      if (!activityTypeId.startsWith("global_")) return undefined;
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts[1];
      }
      return undefined;
    };

    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);

    // First, try to find in user's activity types
    const userActivity = activityTypes.find(
      (type) => type.id === rawActivityId,
    );
    if (userActivity) {
      return userActivity.name;
    }

    // If not found and has globalCategoryID, look in global activities
    const categoryId =
      measurement.globalCategoryID ||
      getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
    if (categoryId) {
      const categoryActivities = globalActivityTypes[categoryId];
      if (categoryActivities) {
        const globalActivity = categoryActivities.find(
          (type) => type.id === rawActivityId,
        );
        if (globalActivity) {
          return globalActivity.name;
        }
      }
    }

    // Fallback across all loaded global categories.
    for (const categoryActivities of Object.values(globalActivityTypes)) {
      const globalActivity = categoryActivities.find(
        (type) => type.id === rawActivityId,
      );
      if (globalActivity) {
        return globalActivity.name;
      }
    }

    // Fall back to the name denormalized on the measurement at log time so it
    // resolves even when the activity's library isn't loaded (e.g. a coach
    // viewing an athlete's custom activity).
    if (measurement.activityName) {
      return measurement.activityName;
    }

    // Debug logging when activity is not found
    console.log("🔍 Unknown Activity Debug:", {
      ActivityTypeID: measurement.ActivityTypeID,
      globalCategoryID: measurement.globalCategoryID,
      userActivityTypesCount: activityTypes.length,
      globalActivityTypesKeys: Object.keys(globalActivityTypes),
      isLoadingData,
    });

    return "Unknown Exercise";
  };

  const getSessionTypeDisplayName = (template: {
    trainingTypeID?: string;
    sessionTypeID?: string;
  }): string | null => {
    const resolved = trainingTypeService.getSessionTrainingTypeName(
      template,
      trainingTypes,
    );
    if (resolved) return resolved;

    // Legacy composite IDs: `${sportId}:${sessionTypeName}`
    const trimmed = template.sessionTypeID?.trim();
    if (!trimmed) return null;
    if (trimmed.includes(":")) {
      const parts = trimmed.split(":");
      // In case a session type name itself contains ':' (unlikely), preserve the rest.
      return parts.slice(1).join(":").trim() || null;
    }

    // Fallback: return as-is (covers legacy formats)
    return trimmed;
  };

  const getTemplateDisplayName = (template: SessionTemplate): string => {
    return (
      asDisplayNameOrNull(template.name) ||
      getSessionTypeDisplayName(template) ||
      (template.ActivityMeasurementList?.[0]
        ? getActivityName(template.ActivityMeasurementList[0])
        : "") ||
      "Untitled"
    );
  };

  // Update dropdown position when it opens or window resizes
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (showDropdown && inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
        });
      }
    };

    const handleScroll = (event: Event) => {
      // Don't reposition if scrolling inside the dropdown itself
      if (portalDropdownRef.current?.contains(event.target as Node)) {
        return;
      }
      updateDropdownPosition();
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showDropdown]);

  // Auto-scroll is no longer needed since actions are visible by default

  // Debug: Log when activity types change
  useEffect(() => {
    // console.log("🔍 Activity types updated:", {
    //   userActivityTypesCount: activityTypes.length,
    //   globalActivityTypesKeys: Object.keys(globalActivityTypes),
    //   isLoadingData,
    // });
  }, [activityTypes, globalActivityTypes, isLoadingData]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser?.uid) return;

      try {
        setIsLoadingData(true);

        const organisationID = (existingSession as any)?.organisationID as
          | string
          | undefined;

        const [activityTypesData, templatesData] = await Promise.all([
          activityService.fetchActivityTypes(currentUser.uid),
          sessionService.getAllSessionTemplates(currentUser.uid),
        ]);

        let allActivityTypes = activityTypesData;
        if (organisationID) {
          const orgActivityTypes =
            await activityService.fetchCoachActivityTypes(organisationID);
          console.log(
            "🔍 Loaded org activity types for",
            organisationID,
            ":",
            orgActivityTypes.length,
            orgActivityTypes.map((t) => ({ id: t.id, name: t.name })),
          );
          allActivityTypes = [...activityTypesData, ...orgActivityTypes];
        } else {
          console.log(
            "🔍 No organisationID found in existing session, skipping org activity types",
          );
        }

        // console.log(
        //   "🔍 Loaded activity types:",
        //   allActivityTypes.length,
        //   "(user:",
        //   activityTypesData.length,
        //   "org:",
        //   allActivityTypes.length - activityTypesData.length,
        //   ")",
        // );
        setActivityTypes(allActivityTypes);
        setSessionTemplates(templatesData);

        // Fetch global activity types for all categories to ensure names resolve
        // even when a measurement references a pack not selected by this user.
        const categories = await GlobalActivityService.fetchCategories();
        const categoryIds = categories
          .map((category) => category.id)
          .filter((id): id is string => !!id);
        if (categoryIds.length > 0) {
          const allGlobalTypes =
            await GlobalActivityService.fetchActivityTypesForCategories(
              categoryIds,
            );
          const globalTypesMap: Record<string, GlobalActivityType[]> =
            allGlobalTypes.reduce(
              (acc, type) => {
                const categoryId = type.categoryId;
                if (!categoryId) return acc;
                if (!acc[categoryId]) acc[categoryId] = [];
                acc[categoryId].push(type);
                return acc;
              },
              {} as Record<string, GlobalActivityType[]>,
            );
          setGlobalActivityTypes(globalTypesMap);
        } else {
          setGlobalActivityTypes({});
        }

        // Load recent activity IDs from localStorage
        const savedRecentIds = localStorage.getItem(
          `recentActivityIds_${currentUser.uid}`,
        );
        if (savedRecentIds) {
          try {
            const parsedIds = JSON.parse(savedRecentIds);
            if (Array.isArray(parsedIds)) {
              setRecentActivityIds(parsedIds.slice(0, 5));
            }
          } catch (e) {
            console.warn(
              "Failed to parse recent activity IDs from localStorage",
            );
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        setError("Failed to load session data");
      } finally {
        setIsLoadingData(false);
      }
    };

    loadData();
  }, [
    currentUser?.uid,
    refreshKey,
    (existingSession as any)?.organisationID,
  ]);

  // Initialize from existing session
  useEffect(() => {
    if (existingSession) {
      onPlannedMeasurementsChange(
        existingSession.ActivityMeasurementList || [],
      );

      setHasSelectedTemplate(true);
    }
  }, [existingSession?.id]);

  // Initialize from template
  useEffect(() => {
    if (initialTemplate && !existingSession) {
      setSelectedTemplate(initialTemplate);
      onPlannedMeasurementsChange(
        initialTemplate.ActivityMeasurementList || [],
      );

      // Add template activity types to recent activities for "Add Set" buttons
      const templateActivityIds = Array.from(
        new Set(
          (initialTemplate.ActivityMeasurementList || []).map(
            (measurement) => measurement.ActivityTypeID,
          ),
        ),
      );
      setRecentActivityIds((prev) => {
        const combined = [...templateActivityIds, ...prev];
        return Array.from(new Set(combined)).slice(0, 5);
      });

      setHasSelectedTemplate(true);
      setError(null);
    }
  }, [initialTemplate, existingSession]);

  // If measurements are injected externally (e.g. AI-generated), enter
  // "start from scratch" mode so the activities UI is shown immediately.
  useEffect(() => {
    if (
      !existingSession &&
      !initialTemplate &&
      plannedMeasurements.length > 0 &&
      !hasSelectedTemplate
    ) {
      setSelectedTemplate(null);
      setHasSelectedTemplate(true);
      setStartedFromScratch(true);
      setShowTemplateSelection(false);
      setError(null);
    }
  }, [
    plannedMeasurements.length,
    existingSession,
    initialTemplate,
    hasSelectedTemplate,
  ]);

  // Reset template selection when form is reset (but not when starting from scratch)
  useEffect(() => {
    if (
      !existingSession &&
      plannedMeasurements.length === 0 &&
      hasSelectedTemplate &&
      !startedFromScratch
    ) {
      // This means the form was reset, not started from scratch
      setHasSelectedTemplate(false);
      setSelectedTemplate(null);
    }
  }, [
    plannedMeasurements,
    existingSession,
    hasSelectedTemplate,
    startedFromScratch,
  ]);


  // Update recent activities when lastSelectedActivityId changes
  useEffect(() => {
    if (
      lastSelectedActivityId &&
      !recentActivityIds.includes(lastSelectedActivityId)
    ) {
      setRecentActivityIds((prev) => {
        const updated = [lastSelectedActivityId, ...prev].slice(0, 5);
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSelectedActivityId]);

  // Save recent activity IDs to localStorage
  useEffect(() => {
    if (currentUser?.uid && recentActivityIds.length > 0) {
      localStorage.setItem(
        `recentActivityIds_${currentUser.uid}`,
        JSON.stringify(recentActivityIds),
      );
    }
  }, [recentActivityIds, currentUser?.uid]);

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the input container
      if (dropdownRef.current?.contains(target)) {
        return;
      }

      // Check if click is inside the portal dropdown
      if (portalDropdownRef.current?.contains(target)) {
        return;
      }

      setShowDropdown(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Group consecutive measurements with the same ActivityTypeID (and globalCategoryID for pack activities)
  const groupedMeasurements = plannedMeasurements.reduce(
    (groups, measurement, index) => {
      const activityName = getActivityName(measurement);
      // Create a unique key that includes globalCategoryID to differentiate pack activities
      const groupKey = measurement.globalCategoryID
        ? `${measurement.ActivityTypeID}_${measurement.globalCategoryID}`
        : measurement.ActivityTypeID;

      const lastGroup = groups[groups.length - 1];
      const lastGroupKey = lastGroup
        ? lastGroup.globalCategoryID
          ? `${lastGroup.activityTypeId}_${lastGroup.globalCategoryID}`
          : lastGroup.activityTypeId
        : null;

      if (groups.length === 0 || lastGroupKey !== groupKey) {
        groups.push({
          activityTypeId: measurement.ActivityTypeID,
          globalCategoryID: measurement.globalCategoryID,
          activityName,
          measurements: [{ measurement, originalIndex: index }],
          startIndex: index,
          endIndex: index,
        });
      } else {
        lastGroup.measurements.push({ measurement, originalIndex: index });
        lastGroup.endIndex = index;
      }

      return groups;
    },
    [] as Array<{
      activityTypeId: string;
      globalCategoryID?: string;
      activityName: string;
      measurements: Array<{
        measurement: ActivityMeasurement;
        originalIndex: number;
      }>;
      startIndex: number;
      endIndex: number;
    }>,
  );

  const handleUpdateVariable = (
    measurementIndex: number,
    variableIndex: number,
    value: number | null,
    unit?: string,
  ) => {
    if (readOnly) return;

    if (onVariableUpdate) {
      onVariableUpdate(measurementIndex, variableIndex, value, unit);
      return;
    }

    console.log("🔧 handleUpdateVariable called:", {
      measurementIndex,
      variableIndex,
      value,
      unit,
    });
    const updatedMeasurements = [...plannedMeasurements];
    const updatedVariables = [
      ...updatedMeasurements[measurementIndex].variables,
    ];
    updatedVariables[variableIndex] = {
      ...updatedVariables[variableIndex],
      measurement: value,
      ...(unit !== undefined && { unit }), // Only update unit if provided
    };
    updatedMeasurements[measurementIndex] = {
      ...updatedMeasurements[measurementIndex],
      variables: updatedVariables,
    };

    // In logging mode, mark set logged when fully completed and unmark if edited back to incomplete.
    // Hidden variables are coach-only and cannot be edited by the athlete, so
    // they are excluded from the completion check.
    if (isLoggingMode) {
      const isComplete = updatedVariables
        .filter((v) => !v.hidden)
        .every((v) => v.measurement !== null && v.measurement !== undefined);
      updatedMeasurements[measurementIndex] = {
        ...updatedMeasurements[measurementIndex],
        timeLogged: isComplete
          ? updatedMeasurements[measurementIndex].timeLogged ||
            new Date().toISOString()
          : undefined,
      };
    }

    console.log(
      "🔧 Calling onPlannedMeasurementsChange with:",
      updatedMeasurements,
    );
    onPlannedMeasurementsChange(updatedMeasurements);
  };
// Coach-preset (or otherwise pre-filled) sets arrive with every variable
// already set, but timeLogged is only ever stamped as a side effect of
// handleUpdateVariable/onVariableUpdate running, which normally only
// happens when the athlete types into a field. A fully pre-filled row
// gives the athlete nothing to type, so it could never become "complete".
// Re-run the existing update path once per such row (using its own
// current value) so the same completion logic fires without requiring
// an edit or adding any new control.
useEffect(() => {
  if (readOnly || !isLoggingMode) return;

  plannedMeasurements.forEach((measurement, measurementIndex) => {
    if (measurement.timeLogged) return;
    if (measurement.variables.length === 0) return;

    const allFilled = measurement.variables.every(
      (v) => v.measurement !== null && v.measurement !== undefined,
    );
    if (!allFilled) return;

    const lastVariableIndex = measurement.variables.length - 1;
    const lastVariable = measurement.variables[lastVariableIndex];

    if (onVariableUpdate) {
      onVariableUpdate(
        measurementIndex,
        lastVariableIndex,
        lastVariable.measurement,
        lastVariable.unit,
      );
    } else {
      handleUpdateVariable(
        measurementIndex,
        lastVariableIndex,
        lastVariable.measurement,
        lastVariable.unit,
      );
    }
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [plannedMeasurements, isLoggingMode, readOnly]);
  // Update unit for all measurements in a group
  const handleGroupUnitChange = (
    originalIndices: number[],
    variableIndex: number,
    newUnit: string,
  ) => {
    if (readOnly) return;

    const updatedMeasurements = [...plannedMeasurements];
    originalIndices.forEach((originalIndex) => {
      const updatedVariables = [
        ...updatedMeasurements[originalIndex].variables,
      ];
      updatedVariables[variableIndex] = {
        ...updatedVariables[variableIndex],
        unit: newUnit,
      };
      updatedMeasurements[originalIndex] = {
        ...updatedMeasurements[originalIndex],
        variables: updatedVariables,
      };
    });
    onPlannedMeasurementsChange(updatedMeasurements);
  };

  // Recent activities quick-add
  const handleRecentActivityAdd = (
    activityId: string,
    insertAfterIndex?: number,
    globalCategoryID?: string,
  ) => {
    if (readOnly) return;

    if (onAddSet) {
      onAddSet(activityId, insertAfterIndex, globalCategoryID);
      return;
    }

    // Try to find an existing measurement with the same activity ID to copy units from
    const existingMeasurement = plannedMeasurements.find(
      (m) =>
        m.ActivityTypeID === activityId &&
        m.globalCategoryID === globalCategoryID,
    );

    let variables: ActivityVariable[];

    if (existingMeasurement) {
      // Copy units from existing measurement (like duplicate does)
      variables = existingMeasurement.variables.map((variable) => ({
        name: variable.name,
        measurement: null,
        unit: variable.unit, // Use the same unit as the existing measurement
      }));
    } else {
      // No existing measurement, create from activity type definition
      // First try to find in user's activity types
      let activityType = activityTypes.find((type) => type.id === activityId);

      // If not found and globalCategoryID is provided, look in global activities
      if (!activityType && globalCategoryID) {
        const categoryActivities = globalActivityTypes[globalCategoryID];
        if (categoryActivities) {
          activityType = categoryActivities.find(
            (type) => type.id === activityId,
          );
        }
      }

      if (!activityType) return;

      // Check if user prefers imperial units
      const isImperial = userData?.isImperial || false;

      const normalizedVariables = normalizeActivityVariables(
        activityType.variables,
      );

      // Debug logging
      console.log("🔍 Activity Type:", activityType.name);
      console.log("🔍 Raw variables:", activityType.variables);
      console.log("🔍 Normalized variables:", normalizedVariables);

      variables = normalizedVariables.map((variable, index) => {
        const unit =
          variable.defaultUnit || getDefaultUnit(variable.name, isImperial);
        console.log(`🔍 Variable ${index} (${variable.name}):`, {
          defaultUnit: variable.defaultUnit,
          calculatedUnit: unit,
        });
        return {
          name: variable.name,
          measurement: null,
          // Use the variable's defaultUnit if available, otherwise get default based on user preference
          unit: unit,
        };
      });

      // Apply the athlete's saved variable preferences (order/unit/hidden) for
      // self-created sessions only. Coach-assigned sessions keep the coach config.
      const sessionIsCoachAssigned = Boolean(
        (existingSession as any)?.coachIssued ||
          (existingSession as any)?.coachID ||
          (existingSession as any)?.programLinkage,
      );
      if (!sessionIsCoachAssigned) {
        variables = applyPreferenceToVariables(
          variables,
          userData?.activityVariablePreferences?.[
            getActivityPrefKey({
              activityTypeId: activityId,
              globalCategoryId: globalCategoryID,
            })
          ],
        );
      }
    }

    // Carry over supersetGroupId if the existing measurement has one
    const supersetGroupId = existingMeasurement?.supersetGroupId;

    const newMeasurement: ActivityMeasurement = {
      ActivityTypeID: activityId,
      variables: variables,
      ...(globalCategoryID && { globalCategoryID }),
      ...(supersetGroupId && { supersetGroupId }),
    };
    // Denormalize the activity name so other accounts (e.g. a coach) can
    // display it without access to the owner's activity library.
    const stampedName =
      existingMeasurement?.activityName || getActivityName(newMeasurement);
    if (stampedName && stampedName !== "Unknown Activity") {
      newMeasurement.activityName = stampedName;
    }

    let insertIndex: number;

    if (insertAfterIndex !== undefined) {
      // Insert right after the specified index (for specific group)
      insertIndex = insertAfterIndex + 1;
    } else {
      // Find the last index of a measurement with the same ActivityTypeID (fallback behavior)
      insertIndex = plannedMeasurements.length;
      for (let i = plannedMeasurements.length - 1; i >= 0; i--) {
        if (plannedMeasurements[i].ActivityTypeID === activityId) {
          insertIndex = i + 1;
          break;
        }
      }
    }

    // Insert the new measurement at the calculated position
    const updatedMeasurements = [...plannedMeasurements];
    updatedMeasurements.splice(insertIndex, 0, newMeasurement);

    onPlannedMeasurementsChange(updatedMeasurements);
    setLastSelectedActivityId(activityId);
    setError(null);

    // Trigger animation for the newly added row
    setNewlyAddedIndex(insertIndex);
    setTimeout(() => {
      setNewlyAddedIndex(null);
    }, 500); // Animation duration
  };

  // Create a superset by linking two consecutive activity groups with a shared UUID
  const handleCreateSuperset = (groupIndices: number[][]) => {
    if (readOnly) return;
    const supersetId = crypto.randomUUID();
    const allOriginalIndices = groupIndices.flat();
    const updatedMeasurements = plannedMeasurements.map((m, i) =>
      allOriginalIndices.includes(i)
        ? { ...m, supersetGroupId: supersetId }
        : m,
    );
    onPlannedMeasurementsChange(updatedMeasurements);
  };

  // Break a superset by removing supersetGroupId from all measurements with the given ID
  const handleBreakSuperset = (supersetGroupId: string) => {
    if (readOnly) return;
    const updatedMeasurements = plannedMeasurements.map((m) =>
      m.supersetGroupId === supersetGroupId
        ? (() => {
            const { supersetGroupId: _, ...rest } = m;
            return rest;
          })()
        : m,
    );
    onPlannedMeasurementsChange(updatedMeasurements);
  };

  const handleAddActivity = () => {
    if (readOnly) return;

    if (!selectedActivityTypeId) {
      setError("Please select an exercise");
      return;
    }

    const activityType = activityTypes.find(
      (type) => type.id === selectedActivityTypeId,
    );
    if (!activityType) {
      setError("Selected exercise not found");
      return;
    }

    // Create ActivityMeasurement with variables initialized from activityType.variables
    const normalizedVariables = normalizeActivityVariables(
      activityType.variables,
    );
    const variables: ActivityVariable[] = normalizedVariables.map(
      (variable) => ({
        name: variable.name,
        measurement: null,
        unit: variable.defaultUnit,
      }),
    );

    const newMeasurement: ActivityMeasurement = {
      ActivityTypeID: selectedActivityTypeId,
      ...(activityType.name && { activityName: activityType.name }),
      variables: variables,
    };

    onPlannedMeasurementsChange([...plannedMeasurements, newMeasurement]);
    setLastSelectedActivityId(selectedActivityTypeId); // Save for next time
    // Keep the selection so user can quickly add multiple sets
    setShowDropdown(false);
    setError(null);
  };

  const handleRemoveActivity = (index: number) => {
    if (readOnly) return;

    if (onRemoveSet) {
      onRemoveSet(index);
      return;
    }

    // Set the deleting index to trigger animation
    setDeletingIndex(index);

    // Wait for animation to complete before removing
    setTimeout(() => {
      const updatedMeasurements = plannedMeasurements.filter(
        (_, i) => i !== index,
      );
      onPlannedMeasurementsChange(updatedMeasurements);
      setDeletingIndex(null);
    }, 300); // Match the animation duration
  };

  const handleDuplicateActivity = (index: number) => {
    if (readOnly) return;

    if (onDuplicateSet) {
      onDuplicateSet(index);
      return;
    }

    const measurementToDuplicate = plannedMeasurements[index];
    if (!measurementToDuplicate) return;

    // Create a deep copy of the measurement including units and globalCategoryID
    const duplicatedMeasurement: ActivityMeasurement = {
      ActivityTypeID: measurementToDuplicate.ActivityTypeID,
      ...(measurementToDuplicate.activityName && {
        activityName: measurementToDuplicate.activityName,
      }),
      variables: measurementToDuplicate.variables.map((variable) => ({
        name: variable.name,
        measurement: variable.measurement,
        unit: variable.unit, // Preserve the unit
      })),
      ...(measurementToDuplicate.globalCategoryID && {
        globalCategoryID: measurementToDuplicate.globalCategoryID,
      }),
    };

    // Insert the duplicated measurement right after the original
    const updatedMeasurements = [
      ...plannedMeasurements.slice(0, index + 1),
      duplicatedMeasurement,
      ...plannedMeasurements.slice(index + 1),
    ];

    onPlannedMeasurementsChange(updatedMeasurements);
    setError(null);
  };

  const handleActivityTypeCreated = async (
    activityTypeId: string,
    activityTypeName: string,
  ) => {
    // Refresh activity types
    if (currentUser?.uid) {
      try {
        const updatedActivityTypes = await activityService.fetchActivityTypes(
          currentUser.uid,
        );
        setActivityTypes(updatedActivityTypes);

        // Auto-select the newly created activity type
        setSelectedActivityTypeId(activityTypeId);
        setSearchTerm(activityTypeName);
        setShowDropdown(false);
        setError(null);
      } catch (error) {
        console.error("Error refreshing activity types:", error);
        setError("Failed to refresh exercises");
      }
    }
  };

  // Filter activity types based on search term and prioritize last selected
  const filteredActivityTypes = activityTypes
    .filter((activityType) => {
      const searchLower = searchTerm.toLowerCase();
      return activityType.name.toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      // Put last selected activity at the top
      if (a.id === lastSelectedActivityId) return -1;
      if (b.id === lastSelectedActivityId) return 1;
      // Otherwise maintain alphabetical order
      return a.name.localeCompare(b.name);
    });

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedActivityTypeId("");
    setShowDropdown(true);
  };

  // Handle activity selection from dropdown
  const handleActivitySelect = (activityType: ActivityType) => {
    if (activityType.id) {
      setSelectedActivityTypeId(activityType.id);
      setSearchTerm(activityType.name);
      setShowDropdown(false);
    }
  };

  // Handle create activity
  const handleCreateActivity = () => {
    setIsCreateActivityDialogOpen(true);
    setShowDropdown(false);
  };

  // Handle bulk activity toggle
  const handleBulkActivityToggle = (activityId: string) => {
    const newSelectedIds = new Set(selectedActivityIds);
    if (newSelectedIds.has(activityId)) {
      newSelectedIds.delete(activityId);
    } else {
      newSelectedIds.add(activityId);
    }
    setSelectedActivityIds(newSelectedIds);
  };

  // Keep local user activity type cache in sync with what gets added from selector.
  // This prevents freshly-created activities from rendering as "Unknown Activity".
  const upsertUserActivityTypes = (typesToUpsert: ActivityType[]) => {
    setActivityTypes((prev) => {
      const byId = new Map<string, ActivityType>();

      prev.forEach((type) => {
        if (type.id) {
          byId.set(type.id, type);
        }
      });

      typesToUpsert.forEach((type) => {
        if (type.id && !type.isFromPack) {
          byId.set(type.id, type);
        }
      });

      return Array.from(byId.values());
    });
  };

  // Handle bulk add activities
  const handleBulkAddActivities = () => {
    const newMeasurements: ActivityMeasurement[] = [];

    selectedActivityIds.forEach((activityId) => {
      const activityType = activityTypes.find((type) => type.id === activityId);
      if (activityType) {
        const normalizedVariables = normalizeActivityVariables(
          activityType.variables,
        );
        const variables: ActivityVariable[] = normalizedVariables.map(
          (variable) => ({
            name: variable.name,
            measurement: null,
            unit: variable.defaultUnit,
          }),
        );

        const newMeasurement: ActivityMeasurement = {
          ActivityTypeID: activityId,
          ...(activityType.name && { activityName: activityType.name }),
          variables: variables,
        };

        newMeasurements.push(newMeasurement);
      }
    });

    onPlannedMeasurementsChange([...plannedMeasurements, ...newMeasurements]);
    setSelectedActivityIds(new Set());
    setBulkSelectionMode(false);
    setError(null);
  };

  // ----- AI dictation helper -------------------------------------------------
  // Snapshot of the activities the AI is allowed to reference. Includes the
  // user's own activities plus global pack activities the user is enrolled
  // in (via `userData.adminActivityCatIDs`). Pack activities expose their
  // raw id + globalCategoryID separately so the AI can produce IDs that
  // match the storage convention used by the rest of this component.
  const aiAvailableActivities = useMemo(() => {
    const user = activityTypes
      .filter((a) => a.id && !a.isFromPack)
      .map((a) => ({
        id: a.id!,
        name: a.name,
        variables: normalizeActivityVariables(a.variables).map((v) => ({
          name: v.name,
          ...(v.defaultUnit ? { defaultUnit: v.defaultUnit } : {}),
        })),
      }));
    const packIds = userData?.adminActivityCatIDs || [];
    const global: Array<{
      id: string;
      globalCategoryID: string;
      name: string;
      variables: { name: string; defaultUnit?: string }[];
    }> = [];
    for (const catId of packIds) {
      const acts = globalActivityTypes[catId];
      if (!acts) continue;
      for (const a of acts) {
        if (!a.id) continue;
        global.push({
          id: a.id,
          globalCategoryID: catId,
          name: a.name,
          variables: normalizeActivityVariables(a.variables).map((v) => ({
            name: v.name,
            ...(v.defaultUnit ? { defaultUnit: v.defaultUnit } : {}),
          })),
        });
      }
    }
    return { user, global };
  }, [activityTypes, globalActivityTypes, userData?.adminActivityCatIDs]);

  // Snapshot of the current measurements the AI can update (by index).
  // `setNumber` is a 1-based counter within each activity group (in
  // document order), so the AI can map phrases like "the 3rd set of
  // dumbbell curls" to the right `measurementIndex` without having to
  // count entries itself.
  const aiCurrentMeasurements = useMemo(() => {
    const setCounts = new Map<string, number>();
    return plannedMeasurements.map((m, i) => {
      const groupKey = `${m.ActivityTypeID}|${m.globalCategoryID ?? ""}`;
      const setNumber = (setCounts.get(groupKey) ?? 0) + 1;
      setCounts.set(groupKey, setNumber);
      return {
        measurementIndex: i,
        setNumber,
        activityName: getActivityName(m),
        ActivityTypeID: m.ActivityTypeID,
        ...(m.globalCategoryID ? { globalCategoryID: m.globalCategoryID } : {}),
        ...(m.supersetGroupId ? { supersetGroupId: m.supersetGroupId } : {}),
        ...(m.timeLogged ? { timeLogged: m.timeLogged } : {}),
        variables: m.variables.map((v) => ({
          name: v.name,
          currentValue: v.measurement,
          ...(v.unit ? { unit: v.unit } : {}),
        })),
      };
    });
    // getActivityName depends on activityTypes / globalActivityTypes /
    // isLoadingData, so we include those in the dep list to keep the
    // snapshot fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannedMeasurements, activityTypes, globalActivityTypes, isLoadingData]);

  // Lookup helpers for handler validation. Indexed by `${id}|${globalCatId||''}`.
  const aiActivityLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        globalCategoryID?: string;
        name: string;
        variables: { name: string; defaultUnit?: string }[];
      }
    >();
    for (const a of aiAvailableActivities.user) {
      map.set(`${a.id}|`, a);
    }
    for (const a of aiAvailableActivities.global) {
      map.set(`${a.id}|${a.globalCategoryID}`, a);
    }
    return map;
  }, [aiAvailableActivities]);

  const aiLogFunctions = useMemo<AILogFunction[]>(
    () => [
      {
        declaration: {
          name: "applyChanges",
          description:
            "Apply one or more changes to the session in a single atomic call: update existing measurements, add new measurement sets, and/or create a brand-new user activity type. Pass only the keys you need; omit the rest. Always uses ActivityTypeIDs and variable names from the snapshot embedded in the system instruction.",
          parameters: Schema.object({
            properties: {
              updates: Schema.array({
                description:
                  "Update existing measurements. Each entry references an existing row by its `measurementIndex` and replaces the listed variables. Variables not listed are left untouched.",
                items: Schema.object({
                  properties: {
                    measurementIndex: Schema.integer({
                      description:
                        "0-based index of the measurement to update, taken from the 'Current measurements' snapshot.",
                    }),
                    variables: Schema.array({
                      description:
                        "Variables to update on this measurement. Each `name` MUST match a variable defined for the activity at that index.",
                      items: Schema.object({
                        properties: {
                          name: Schema.string({
                            description: "Variable name (e.g. 'Reps').",
                          }),
                          measurement: Schema.number({
                            description:
                              "Numeric value, or omit to clear the value.",
                          }),
                          unit: Schema.string({
                            description:
                              "Optional unit override. Omit to keep the existing unit.",
                          }),
                        },
                        optionalProperties: ["measurement", "unit"],
                      }),
                    }),
                  },
                }),
              }),
              adds: Schema.array({
                description:
                  "Append new measurement sets. ONE entry = ONE set; for '3 sets of 10 squats' provide 3 entries. Use this for both existing activities and the just-created activity (referenced via ActivityTypeID = 'NEW' alongside `newActivity`). For supersets, give the entries you want grouped together the same `supersetTag` value (e.g. 'A').",
                items: Schema.object({
                  properties: {
                    ActivityTypeID: Schema.string({
                      description:
                        "Activity to log. Use an `id` from 'Available activities', or the literal string 'NEW' to attach this set to the activity type provided in `newActivity`.",
                    }),
                    globalCategoryID: Schema.string({
                      description:
                        "REQUIRED when ActivityTypeID refers to a global/pack activity (use the `globalCategoryID` shown in the snapshot). Omit for user activities and for 'NEW'.",
                    }),
                    supersetTag: Schema.string({
                      description:
                        "Optional local tag that groups this entry with others sharing the same tag into a SUPERSET. Use a short label (e.g. 'A', 'B', '1'). The tag is local to this `applyChanges` call only - we generate a real supersetGroupId server-side. To attach these new sets to an EXISTING superset, also list the same tag in top-level `supersets` with `addToExistingSupersetGroupId` set.",
                    }),
                    variables: Schema.array({
                      description:
                        "Per-variable values for this set. Each `name` MUST match the activity's variable definition. Use `null`/omit `measurement` if the user did not supply a value.",
                      items: Schema.object({
                        properties: {
                          name: Schema.string({
                            description: "Variable name.",
                          }),
                          measurement: Schema.number({
                            description:
                              "Numeric value, or omit if not supplied.",
                          }),
                          unit: Schema.string({
                            description:
                              "Unit. Defaults to the activity's defaultUnit if omitted.",
                          }),
                        },
                        optionalProperties: ["measurement", "unit"],
                      }),
                    }),
                  },
                  optionalProperties: [
                    "globalCategoryID",
                    "supersetTag",
                    "variables",
                  ],
                }),
              }),
              newActivity: Schema.object({
                description:
                  "Create ONE new user activity type. Only set this when no existing activity is a reasonable fit OR the user explicitly asked for a new activity. To log sets against the new activity, also pass `adds` entries with ActivityTypeID = 'NEW'.",
                properties: {
                  name: Schema.string({
                    description: "Activity name (e.g. 'Pull Up', 'Box Jump').",
                  }),
                  variables: Schema.array({
                    description:
                      "Variable definitions for the new activity. Common combos: [{name:'Reps'}], [{name:'Weight', defaultUnit:'kg'}, {name:'Reps'}], [{name:'Distance', defaultUnit:'km'}, {name:'Time', defaultUnit:'min'}], [{name:'Time', defaultUnit:'sec'}].",
                    items: Schema.object({
                      properties: {
                        name: Schema.string({
                          description: "Variable name.",
                        }),
                        defaultUnit: Schema.string({
                          description:
                            "Default unit for this variable (e.g. 'kg', 'reps', 'km', 'min', 'sec'). Omit for unitless variables like 'Reps'.",
                        }),
                      },
                      optionalProperties: ["defaultUnit"],
                    }),
                  }),
                },
              }),
              supersets: Schema.array({
                description:
                  "Group rows into a SUPERSET (alternating sets). Use ONE entry per superset you want to create or extend. The `tag` is a local label shared with `adds[].supersetTag`. To CREATE a brand-new superset, list `existingMeasurementIndexes` and/or use the tag in `adds`. To EXTEND an existing superset (e.g. 'add another round of curls and dips to the superset'), also pass its current `supersetGroupId` as `addToExistingSupersetGroupId` (look it up in the snapshot).",
                items: Schema.object({
                  properties: {
                    tag: Schema.string({
                      description:
                        "Local tag shared with `adds[].supersetTag`. Just a short label like 'A' or 'B'.",
                    }),
                    addToExistingSupersetGroupId: Schema.string({
                      description:
                        "Optional: the `supersetGroupId` of an EXISTING superset (from the snapshot) you want to extend. Omit to create a fresh superset.",
                    }),
                    existingMeasurementIndexes: Schema.array({
                      description:
                        "Optional: existing measurements (by `measurementIndex`) to include in this superset.",
                      items: Schema.integer({
                        description: "0-based `measurementIndex` of the row.",
                      }),
                    }),
                  },
                  optionalProperties: [
                    "addToExistingSupersetGroupId",
                    "existingMeasurementIndexes",
                  ],
                }),
              }),
              breakSupersets: Schema.array({
                description:
                  "Dissolve one or more EXISTING supersets. Each value is a `supersetGroupId` from the snapshot; every row sharing that ID is unlinked. Use when the user says 'break the superset' / 'separate curls and dips'.",
                items: Schema.string({
                  description: "An existing `supersetGroupId` to dissolve.",
                }),
              }),
              moves: Schema.array({
                description:
                  "Re-order rows within the session. ONE entry = one contiguous block being moved. Use this for 'move back squats before box jumps', 'put pull-ups at the start', 'send the warmup to the end', etc. Typical case: to move an entire activity group, include EVERY `measurementIndex` belonging to that activity in `sourceMeasurementIndexes` so all of its sets stay together.",
                items: Schema.object({
                  properties: {
                    sourceMeasurementIndexes: Schema.array({
                      description:
                        "0-based `measurementIndex`(es) of the rows to move, taken from the snapshot. Order is preserved among the moved rows.",
                      items: Schema.integer({
                        description: "A measurementIndex from the snapshot.",
                      }),
                    }),
                    position: Schema.string({
                      description:
                        "Where to place the moved rows. MUST be exactly one of: 'before' (insert just before targetMeasurementIndex), 'after' (insert just after targetMeasurementIndex), 'start' (top of the list), 'end' (bottom of the list).",
                    }),
                    targetMeasurementIndex: Schema.integer({
                      description:
                        "REQUIRED when position is 'before' or 'after'. The measurementIndex of the anchor row from the snapshot. For 'before', use the FIRST measurementIndex of the target activity; for 'after', use the LAST one. Omit for position 'start' / 'end'.",
                    }),
                  },
                  optionalProperties: ["targetMeasurementIndex"],
                }),
              }),
            },
            optionalProperties: [
              "updates",
              "adds",
              "newActivity",
              "supersets",
              "breakSupersets",
              "moves",
            ],
          }),
        },
        handler: async (args) => {
          if (readOnly) {
            return {
              success: false,
              message: "This session is read-only.",
            };
          }
          if (!currentUser?.uid) {
            return {
              success: false,
              message: "No signed-in user; cannot apply changes.",
            };
          }

          const updates = Array.isArray(args.updates) ? args.updates : [];
          const adds = Array.isArray(args.adds) ? args.adds : [];
          const supersetSpecs = Array.isArray(args.supersets)
            ? args.supersets
            : [];
          const breakSupersetIds = Array.isArray(args.breakSupersets)
            ? (args.breakSupersets as unknown[]).filter(
                (v): v is string => typeof v === "string" && v.trim().length > 0,
              )
            : [];
          const moves = Array.isArray(args.moves) ? args.moves : [];
          const newActivity =
            args.newActivity && typeof args.newActivity === "object"
              ? (args.newActivity as {
                  name?: unknown;
                  variables?: unknown;
                })
              : null;

          if (
            updates.length === 0 &&
            adds.length === 0 &&
            !newActivity &&
            supersetSpecs.length === 0 &&
            breakSupersetIds.length === 0 &&
            moves.length === 0
          ) {
            return {
              success: false,
              message:
                "Nothing to do. Provide `updates`, `adds`, `newActivity`, `supersets`, `breakSupersets`, and/or `moves`.",
            };
          }

          // Start from current measurements; we'll mutate a working copy.
          let working: ActivityMeasurement[] = plannedMeasurements.map((m) => ({
            ...m,
            variables: m.variables.map((v) => ({ ...v })),
          }));

          // 1. Apply updates.
          const updateErrors: string[] = [];
          for (const raw of updates) {
            if (!raw || typeof raw !== "object") continue;
            const u = raw as {
              measurementIndex?: unknown;
              variables?: unknown;
            };
            const idx =
              typeof u.measurementIndex === "number"
                ? u.measurementIndex
                : Number(u.measurementIndex);
            if (!Number.isInteger(idx) || idx < 0 || idx >= working.length) {
              updateErrors.push(
                `measurementIndex ${u.measurementIndex} is out of range.`,
              );
              continue;
            }
            const target = working[idx];
            const variableUpdates = Array.isArray(u.variables)
              ? u.variables
              : [];
            const allowedNames = new Map(
              target.variables.map((v) => [v.name.toLowerCase(), v.name]),
            );
            const updatedVariables = target.variables.map((v) => ({ ...v }));
            for (const vRaw of variableUpdates) {
              if (!vRaw || typeof vRaw !== "object") continue;
              const v = vRaw as {
                name?: unknown;
                measurement?: unknown;
                unit?: unknown;
              };
              const rawName = typeof v.name === "string" ? v.name.trim() : "";
              const canonical = allowedNames.get(rawName.toLowerCase());
              if (!canonical) {
                updateErrors.push(
                  `Variable "${rawName}" is not on activity "${getActivityName(
                    target,
                  )}".`,
                );
                continue;
              }
              const slot = updatedVariables.findIndex(
                (vv) => vv.name === canonical,
              );
              if (slot < 0) continue;
              let measurement: number | null =
                updatedVariables[slot].measurement ?? null;
              if (v.measurement === null) {
                measurement = null;
              } else if (v.measurement !== undefined) {
                const num =
                  typeof v.measurement === "number"
                    ? v.measurement
                    : Number(v.measurement);
                measurement = Number.isFinite(num) ? num : null;
              }
              const unit =
                typeof v.unit === "string" && v.unit.trim()
                  ? v.unit.trim()
                  : updatedVariables[slot].unit;
              updatedVariables[slot] = {
                ...updatedVariables[slot],
                measurement,
                ...(unit ? { unit } : {}),
              };
            }
            working[idx] = {
              ...target,
              variables: updatedVariables,
            };
            // Mirror the logging-mode auto-mark logic from handleUpdateVariable.
            if (isLoggingMode) {
              const isComplete = updatedVariables
                .filter((v) => !v.hidden)
                .every(
                  (v) => v.measurement !== null && v.measurement !== undefined,
                );
              working[idx] = {
                ...working[idx],
                timeLogged: isComplete
                  ? working[idx].timeLogged || new Date().toISOString()
                  : undefined,
              };
            }
          }

          // 2. Optionally create a new user activity type.
          let newActivityID: string | null = null;
          let newActivityName: string | null = null;
          let newActivityVarDefs: { name: string; defaultUnit?: string }[] = [];
          if (newActivity) {
            const name =
              typeof newActivity.name === "string"
                ? newActivity.name.trim()
                : "";
            const rawVars = Array.isArray(newActivity.variables)
              ? newActivity.variables
              : [];
            const varDefs = rawVars
              .filter((v) => v && typeof v === "object")
              .map((v) => {
                const vv = v as { name?: unknown; defaultUnit?: unknown };
                return {
                  name: typeof vv.name === "string" ? vv.name.trim() : "",
                  ...(typeof vv.defaultUnit === "string" &&
                  vv.defaultUnit.trim()
                    ? { defaultUnit: vv.defaultUnit.trim() }
                    : {}),
                };
              })
              .filter((v) => v.name.length > 0);

            if (!name) {
              return {
                success: false,
                message:
                  "`newActivity.name` is required when creating a new activity.",
              };
            }
            if (varDefs.length === 0) {
              return {
                success: false,
                message:
                  "`newActivity.variables` must contain at least one variable.",
              };
            }

            // The persisted ActivityVariableDefinition requires a string
            // `defaultUnit` (not undefined), so substitute "" for any
            // variable the AI didn't give a unit for.
            const persistableVarDefs = varDefs.map((v) => ({
              name: v.name,
              defaultUnit: v.defaultUnit ?? "",
            }));

            try {
              newActivityID = await activityService.createActivityType(
                currentUser.uid,
                {
                  name,
                  description: "",
                  variables: persistableVarDefs,
                },
              );
              newActivityName = name;
              newActivityVarDefs = varDefs;
              // Refresh local cache so the new activity shows up everywhere
              // and getActivityName resolves to its real name.
              upsertUserActivityTypes([
                {
                  id: newActivityID,
                  name,
                  description: "",
                  variables: persistableVarDefs,
                },
              ]);
            } catch (err: any) {
              console.error("AI applyChanges createActivityType failed:", err);
              return {
                success: false,
                message: err?.message ?? "Failed to create new activity type.",
              };
            }
          }

          // 3. Apply adds.
          const addErrors: string[] = [];
          const isImperial = userData?.isImperial || false;
          // Track the newly-created measurement objects (by reference) so we
          // can stamp them with a supersetGroupId after the adds loop, once
          // all tags are resolved.
          const newAddsByTag = new Map<string, ActivityMeasurement[]>();
          for (const raw of adds) {
            if (!raw || typeof raw !== "object") continue;
            const s = raw as {
              ActivityTypeID?: unknown;
              globalCategoryID?: unknown;
              supersetTag?: unknown;
              variables?: unknown;
            };
            const rawID =
              typeof s.ActivityTypeID === "string"
                ? s.ActivityTypeID.trim()
                : "";
            if (!rawID) {
              addErrors.push("Set entry missing ActivityTypeID.");
              continue;
            }
            const supersetTag =
              typeof s.supersetTag === "string" && s.supersetTag.trim()
                ? s.supersetTag.trim()
                : "";

            let resolvedID: string;
            let resolvedGlobalCatID: string | undefined;
            let activityVarDefs: { name: string; defaultUnit?: string }[];
            let activityName: string;

            if (rawID === "NEW") {
              if (!newActivityID) {
                addErrors.push(
                  "Set references 'NEW' but no `newActivity` was provided.",
                );
                continue;
              }
              resolvedID = newActivityID;
              activityVarDefs = newActivityVarDefs;
              activityName = newActivityName ?? "New activity";
            } else {
              const globalCatID =
                typeof s.globalCategoryID === "string" &&
                s.globalCategoryID.trim()
                  ? s.globalCategoryID.trim()
                  : undefined;
              const lookupKey = `${rawID}|${globalCatID ?? ""}`;
              const lookup = aiActivityLookup.get(lookupKey);
              if (!lookup) {
                addErrors.push(
                  `Unknown activity "${rawID}"${
                    globalCatID ? ` (globalCategoryID ${globalCatID})` : ""
                  }.`,
                );
                continue;
              }
              resolvedID = lookup.id;
              resolvedGlobalCatID = lookup.globalCategoryID;
              activityVarDefs = lookup.variables;
              activityName = lookup.name;
            }

            const allowedNames = new Map(
              activityVarDefs.map((v) => [v.name.toLowerCase(), v]),
            );
            const variables: ActivityVariable[] = activityVarDefs.map(
              (def) => ({
                name: def.name,
                measurement: null,
                unit: def.defaultUnit || getDefaultUnit(def.name, isImperial),
              }),
            );

            const rawVars = Array.isArray(s.variables) ? s.variables : [];
            for (const vRaw of rawVars) {
              if (!vRaw || typeof vRaw !== "object") continue;
              const v = vRaw as {
                name?: unknown;
                measurement?: unknown;
                unit?: unknown;
              };
              const rawName = typeof v.name === "string" ? v.name.trim() : "";
              const def = allowedNames.get(rawName.toLowerCase());
              if (!def) continue;
              const slot = variables.findIndex((vv) => vv.name === def.name);
              if (slot < 0) continue;
              let measurement: number | null = null;
              if (v.measurement !== undefined && v.measurement !== null) {
                const num =
                  typeof v.measurement === "number"
                    ? v.measurement
                    : Number(v.measurement);
                measurement = Number.isFinite(num) ? num : null;
              }
              const unit =
                typeof v.unit === "string" && v.unit.trim()
                  ? v.unit.trim()
                  : variables[slot].unit;
              variables[slot] = {
                name: def.name,
                measurement,
                ...(unit ? { unit } : {}),
              };
            }

            const newMeasurement: ActivityMeasurement = {
              ActivityTypeID: resolvedID,
              ...(activityName ? { activityName } : {}),
              variables,
              ...(resolvedGlobalCatID
                ? { globalCategoryID: resolvedGlobalCatID }
                : {}),
            };
            if (isLoggingMode) {
              const isComplete = variables
                .filter((v) => !v.hidden)
                .every(
                  (v) => v.measurement !== null && v.measurement !== undefined,
                );
              if (isComplete) {
                newMeasurement.timeLogged = new Date().toISOString();
              }
            }
            // Append to the end of the existing group of the same activity
            // so related sets stay together visually.
            let insertIndex = working.length;
            for (let i = working.length - 1; i >= 0; i--) {
              if (
                working[i].ActivityTypeID === resolvedID &&
                working[i].globalCategoryID === resolvedGlobalCatID
              ) {
                insertIndex = i + 1;
                break;
              }
            }
            working = [
              ...working.slice(0, insertIndex),
              newMeasurement,
              ...working.slice(insertIndex),
            ];
            // Track for the recently-used quick-add buttons.
            if (resolvedID) setLastSelectedActivityId(resolvedID);
            // Stash the new measurement so we can attach a supersetGroupId
            // to it once all tags in this call are resolved.
            if (supersetTag) {
              const bucket = newAddsByTag.get(supersetTag);
              if (bucket) bucket.push(newMeasurement);
              else newAddsByTag.set(supersetTag, [newMeasurement]);
            }
          }

          // 4. Resolve supersets - assign a single supersetGroupId per local
          // tag, attach to any tagged adds AND to any existing
          // measurementIndexes listed in `supersets`. Tags can also extend
          // an existing superset via `addToExistingSupersetGroupId`.
          const supersetErrors: string[] = [];
          const tagToGroupId = new Map<string, string>();
          // First pass: register any supersets[] entries (so tags with an
          // explicit `addToExistingSupersetGroupId` are seeded before
          // unmatched adds-only tags get fresh UUIDs).
          type SupersetSpec = {
            tag: string;
            addToExisting?: string;
            existingIndexes: number[];
          };
          const parsedSupersets: SupersetSpec[] = [];
          for (const raw of supersetSpecs) {
            if (!raw || typeof raw !== "object") continue;
            const sp = raw as {
              tag?: unknown;
              addToExistingSupersetGroupId?: unknown;
              existingMeasurementIndexes?: unknown;
            };
            const tag =
              typeof sp.tag === "string" && sp.tag.trim() ? sp.tag.trim() : "";
            if (!tag) {
              supersetErrors.push("`supersets` entry missing `tag`.");
              continue;
            }
            const addToExisting =
              typeof sp.addToExistingSupersetGroupId === "string" &&
              sp.addToExistingSupersetGroupId.trim()
                ? sp.addToExistingSupersetGroupId.trim()
                : undefined;
            const rawIndexes = Array.isArray(sp.existingMeasurementIndexes)
              ? sp.existingMeasurementIndexes
              : [];
            const existingIndexes: number[] = [];
            for (const ri of rawIndexes) {
              const n = typeof ri === "number" ? ri : Number(ri);
              if (!Number.isInteger(n) || n < 0 || n >= working.length) {
                supersetErrors.push(
                  `supersets.existingMeasurementIndexes value ${ri} is out of range.`,
                );
                continue;
              }
              existingIndexes.push(n);
            }
            parsedSupersets.push({
              tag,
              addToExisting,
              existingIndexes,
            });
            if (addToExisting && !tagToGroupId.has(tag)) {
              tagToGroupId.set(tag, addToExisting);
            }
          }

          const generateGroupId = (): string =>
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
              ? crypto.randomUUID()
              : `superset-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2, 10)}`;

          // Make sure every tag (from supersets[] or adds) has a UUID.
          const allTags = Array.from(
            new Set<string>([
              ...parsedSupersets.map((s) => s.tag),
              ...Array.from(newAddsByTag.keys()),
            ]),
          );
          for (const tag of allTags) {
            if (!tagToGroupId.has(tag)) {
              tagToGroupId.set(tag, generateGroupId());
            }
          }

          // Attach UUIDs to new adds.
          newAddsByTag.forEach((measurements, tag) => {
            const gid = tagToGroupId.get(tag);
            if (!gid) return;
            for (const m of measurements) {
              m.supersetGroupId = gid;
            }
          });

          // Attach UUIDs to existing measurements.
          for (const spec of parsedSupersets) {
            const gid = tagToGroupId.get(spec.tag);
            if (!gid) continue;
            for (const idx of spec.existingIndexes) {
              working[idx] = {
                ...working[idx],
                supersetGroupId: gid,
              };
            }
          }

          // 5. Break any explicitly-requested existing supersets. Done
          // before moves so the AI can split a group and re-form it in
          // the same call.
          if (breakSupersetIds.length > 0) {
            const breakSet = new Set(breakSupersetIds);
            working = working.map((m) => {
              if (m.supersetGroupId && breakSet.has(m.supersetGroupId)) {
                const { supersetGroupId: _ignored, ...rest } = m;
                return rest;
              }
              return m;
            });
          }

          // 6. Re-order rows. Processed sequentially against the current
          // `working` array. Source/target indexes refer to the snapshot
          // the AI saw, which is generally fine because adds only shift
          // indexes within the inserted-into activity group; for the
          // typical single-move case the indexes line up.
          const moveErrors: string[] = [];
          let movesApplied = 0;
          for (const raw of moves) {
            if (!raw || typeof raw !== "object") continue;
            const mv = raw as {
              sourceMeasurementIndexes?: unknown;
              position?: unknown;
              targetMeasurementIndex?: unknown;
            };

            const rawSources = Array.isArray(mv.sourceMeasurementIndexes)
              ? mv.sourceMeasurementIndexes
              : [];
            const sourceIdxs: number[] = [];
            for (const r of rawSources) {
              const n = typeof r === "number" ? r : Number(r);
              if (!Number.isInteger(n) || n < 0 || n >= working.length) {
                moveErrors.push(
                  `move.sourceMeasurementIndexes value ${r} is out of range.`,
                );
                continue;
              }
              if (!sourceIdxs.includes(n)) sourceIdxs.push(n);
            }
            if (sourceIdxs.length === 0) {
              moveErrors.push("move has no valid source indexes; skipped.");
              continue;
            }

            const positionRaw =
              typeof mv.position === "string"
                ? mv.position.trim().toLowerCase()
                : "";
            if (!["before", "after", "start", "end"].includes(positionRaw)) {
              moveErrors.push(
                `move.position "${mv.position}" is invalid (expected 'before'|'after'|'start'|'end').`,
              );
              continue;
            }
            const position = positionRaw as
              | "before"
              | "after"
              | "start"
              | "end";

            let targetIdx: number | null = null;
            if (position === "before" || position === "after") {
              const t =
                typeof mv.targetMeasurementIndex === "number"
                  ? mv.targetMeasurementIndex
                  : Number(mv.targetMeasurementIndex);
              if (!Number.isInteger(t) || t < 0 || t >= working.length) {
                moveErrors.push(
                  `move.targetMeasurementIndex ${mv.targetMeasurementIndex} is out of range.`,
                );
                continue;
              }
              if (sourceIdxs.includes(t)) {
                moveErrors.push(
                  "move.targetMeasurementIndex overlaps a source index; skipped.",
                );
                continue;
              }
              targetIdx = t;
            }

            // Pluck source rows (ascending) and rebuild the list without
            // them, then splice the moved block in at the right spot.
            const sortedSources = [...sourceIdxs].sort((a, b) => a - b);
            const sourceSet = new Set(sortedSources);
            const snapshot = working;
            const sourceRows = sortedSources.map((i) => snapshot[i]);
            const targetRow = targetIdx !== null ? working[targetIdx] : null;
            const remaining: ActivityMeasurement[] = [];
            for (let i = 0; i < working.length; i++) {
              if (!sourceSet.has(i)) remaining.push(working[i]);
            }

            let insertIdx: number;
            if (position === "start") {
              insertIdx = 0;
            } else if (position === "end") {
              insertIdx = remaining.length;
            } else {
              if (!targetRow) {
                moveErrors.push("move target row unavailable; skipped.");
                continue;
              }
              const newTargetIdx = remaining.indexOf(targetRow);
              if (newTargetIdx < 0) {
                moveErrors.push(
                  "move target was not found after removing source rows; skipped.",
                );
                continue;
              }
              insertIdx = position === "before" ? newTargetIdx : newTargetIdx + 1;
            }

            working = [
              ...remaining.slice(0, insertIdx),
              ...sourceRows,
              ...remaining.slice(insertIdx),
            ];
            movesApplied += 1;
          }

          // Hand the fully-updated list off to the parent. If the parent
          // wants to persist (e.g. the live logging page that auto-saves
          // per row), it can implement `onAIApplyChanges` and the AI will
          // wait on that persistence and surface failures back to the
          // model. Otherwise we fall through to the regular
          // `onPlannedMeasurementsChange` path (parents that save on a
          // dedicated button keep working unchanged).
          if (onAIApplyChanges) {
            try {
              await onAIApplyChanges(working);
            } catch (err: any) {
              console.error("AI onAIApplyChanges failed:", err);
              return {
                success: false,
                message:
                  err?.message ??
                  "Applied changes locally but failed to save them.",
              };
            }
          } else {
            onPlannedMeasurementsChange(working);
          }
          setError(null);

          const summary: string[] = [];
          if (updates.length > 0)
            summary.push(
              `${updates.length} update${updates.length === 1 ? "" : "s"}`,
            );
          if (newActivityID)
            summary.push(`created activity "${newActivityName}"`);
          if (adds.length > 0)
            summary.push(
              `${adds.length} new set${adds.length === 1 ? "" : "s"}`,
            );
          // Count distinct new/extended supersets so the AI knows the
          // grouping took effect.
          const distinctSupersetIds = new Set<string>([
            ...Array.from(tagToGroupId.values()),
          ]);
          if (distinctSupersetIds.size > 0)
            summary.push(
              `${distinctSupersetIds.size} superset${
                distinctSupersetIds.size === 1 ? "" : "s"
              }`,
            );
          if (breakSupersetIds.length > 0)
            summary.push(
              `broke ${breakSupersetIds.length} superset${
                breakSupersetIds.length === 1 ? "" : "s"
              }`,
            );
          if (movesApplied > 0)
            summary.push(
              `moved ${movesApplied} block${movesApplied === 1 ? "" : "s"}`,
            );

          return {
            success: true,
            message: `Applied ${summary.join(", ")}.${
              updateErrors.length > 0
                ? ` Update issues: ${updateErrors.join(" ")}`
                : ""
            }${
              addErrors.length > 0 ? ` Add issues: ${addErrors.join(" ")}` : ""
            }${
              supersetErrors.length > 0
                ? ` Superset issues: ${supersetErrors.join(" ")}`
                : ""
            }${
              moveErrors.length > 0 ? ` Move issues: ${moveErrors.join(" ")}` : ""
            }`,
            data: {
              ...(newActivityID ? { newActivityID } : {}),
              ...(updateErrors.length > 0 ? { updateErrors } : {}),
              ...(addErrors.length > 0 ? { addErrors } : {}),
              ...(supersetErrors.length > 0 ? { supersetErrors } : {}),
              ...(moveErrors.length > 0 ? { moveErrors } : {}),
            },
          };
        },
      },
    ],
    // Keep handler in sync with the latest snapshots.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      aiActivityLookup,
      plannedMeasurements,
      readOnly,
      currentUser?.uid,
      isLoggingMode,
      userData?.isImperial,
      onAIApplyChanges,
      onPlannedMeasurementsChange,
    ],
  );

  const aiPrompt = useMemo(() => {
    const userActsLine = aiAvailableActivities.user.length
      ? aiAvailableActivities.user
          .map(
            (a) =>
              `{id:"${a.id}",name:"${a.name}",vars:${JSON.stringify(a.variables)}}`,
          )
          .join("\n")
      : "(none yet)";
    const globalActsLine = aiAvailableActivities.global.length
      ? aiAvailableActivities.global
          .map(
            (a) =>
              `{id:"${a.id}",globalCategoryID:"${a.globalCategoryID}",name:"${a.name}",vars:${JSON.stringify(a.variables)}}`,
          )
          .join("\n")
      : "(none)";
    const measurementsLine = aiCurrentMeasurements.length
      ? aiCurrentMeasurements.map((m) => `${JSON.stringify(m)}`).join("\n")
      : "(empty - no sets logged yet)";

    return [
      "Context: the user is editing the Activity / Set list inside a workout",
      `session in the Peak X app. Mode: ${isLoggingMode ? "logging (writing actual results)" : "planning (designing the session)"}.`,
      ...(isLoggingMode
        ? [
            "In logging mode, every change you apply is saved to the session",
            "automatically (and the row is stamped with the time it was logged",
            "when all variables are filled). Do NOT ask the user to confirm or",
            "press save - just apply the change.",
          ]
        : []),
      "",
      "Your job is to translate the user's spoken update into ONE call to",
      "`applyChanges`. Decide which of these you need:",
      "  - `updates`: when the user is CORRECTING or FILLING IN a value on a",
      "    measurement that ALREADY exists in `Current measurements` (e.g.",
      "    'push ups was 12 reps', 'change the 3rd set of dumbbell curls to",
      "    16 reps not 15', 'the second squat was actually 80kg'). Look up",
      "    the row by `activityName` + `setNumber` (1-based within that",
      "    activity, in the order shown) and use its `measurementIndex`.",
      "    `setNumber` lets you map 'first/second/third/last set of X' to a",
      "    specific row without counting. Only send the variables that",
      "    changed; others are kept as-is.",
      "  - `adds`: when the user wants MORE sets of an existing activity",
      "    (e.g., 'add 3x10 squats' -> 3 entries with the squats",
      "    ActivityTypeID, each with Reps=10). Don't use this to overwrite",
      "    an existing set - that's what `updates` is for.",
      "  - `newActivity`: ONLY when no existing activity is a reasonable",
      "    match OR the user explicitly asked for a new activity (e.g., 'add",
      "    a new pull-up activity'). To log sets against it in the same call,",
      "    pass `adds` with ActivityTypeID = 'NEW'.",
      "  - `supersets` / `adds[].supersetTag`: when the user pairs two or",
      "    more activities into a SUPERSET (alternating sets back-to-back,",
      "    e.g. 'dumbbell curls supersetted with tricep dips', 'curls",
      "    paired with dips', 'A1/A2 superset'). Give every `adds` entry",
      "    that should be in the same superset the SAME `supersetTag`",
      "    string (use 'A' for the first superset, 'B' for a second, etc.",
      "    - tags are local to this call). To make EXISTING measurements",
      "    into a superset, add a `supersets` entry with the same tag plus",
      "    `existingMeasurementIndexes` listing the rows to link. To",
      "    EXTEND an existing superset (e.g. 'add another round of curls",
      "    and dips to the superset'), look up its `supersetGroupId` in",
      "    the snapshot and pass it as `addToExistingSupersetGroupId` on a",
      "    `supersets` entry with the same tag your new adds use.",
      "  - `breakSupersets`: when the user wants to dissolve a superset",
      "    (e.g. 'break the superset', 'separate curls and dips again').",
      "    Pass the existing `supersetGroupId`(s) to dissolve.",
      "  - `moves`: when the user wants to RE-ORDER existing rows (e.g.",
      "    'move back squats ahead of box jumps', 'put pull-ups at the",
      "    start', 'send the warm-up to the end', 'put the dumbbell curls",
      "    after the bench press'). Put EVERY `measurementIndex`",
      "    belonging to the activity being moved into `sourceMeasurementIndexes`",
      "    so all of its sets travel together. For position 'before', use",
      "    the FIRST measurementIndex of the target activity as",
      "    `targetMeasurementIndex`; for 'after', use the LAST. Use",
      "    position 'start' or 'end' (no target) for top/bottom of the",
      "    list. Don't use moves to add or update values - it only",
      "    re-orders.",
      "You can combine any of these in one call.",
      "Use ActivityTypeIDs and variable names exactly as shown below.",
      "",
      "User activities (id is the ActivityTypeID, no globalCategoryID needed):",
      userActsLine,
      "",
      "Global pack activities (use BOTH id AND globalCategoryID together):",
      globalActsLine,
      "",
      "Current measurements (reference rows by `measurementIndex`; the",
      "`setNumber` field is the 1-based set number within that activity;",
      "`supersetGroupId`, when present, means the row is already part of a",
      "superset with every other row sharing that id):",
      measurementsLine,
    ].join("\n");
  }, [aiAvailableActivities, aiCurrentMeasurements, isLoggingMode]);

  // Push AI config to parent when it changes (so parent can render its own AILogButton)
  const prevAiPromptRef = useRef<string>("");
  const prevAiLogFunctionsRef = useRef<AILogFunction[]>([]);

  useEffect(() => {
    if (
      aiPrompt !== prevAiPromptRef.current ||
      aiLogFunctions !== prevAiLogFunctionsRef.current
    ) {
      prevAiPromptRef.current = aiPrompt;
      prevAiLogFunctionsRef.current = aiLogFunctions;
      onAIConfigReady?.(aiPrompt, aiLogFunctions);
    }
  }, [aiPrompt, aiLogFunctions, onAIConfigReady]);
  // ---------------------------------------------------------------------------

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-base-content/60 font-grotesk">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* Hide scrollbars on desktop */}
      <style>{`
        @media (min-width: 640px) {
          .sm\\:scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        }
      `}</style>
      {/* Content */}
      <div className="flex-1 min-h-full overflow-x-hidden relative">
        {/* START COMPONENT */}

        <div className="p-4 pb-8 pt-0 px-8 bg-base-100">
          {/* Template Selection Section - Only show when creating new session */}
          {!isLoggingMode && !existingSession && (
            <div className="pt-0">
              <div className="space-y-3">
                {/* Template Dropdown or Selected Template Display */}

                {!selectedTemplate && !hasSelectedTemplate && (
                  <div className="space-y-3 min-h-[60vh]">
                    <p className="pb-4 px-2 pt-4 text-lg">
                      How do you want to start?
                    </p>
                    {/* Template Dropdown (searchable) */}
                    <SearchableSelect
                      options={sessionTemplates
                        .filter((t) => !!t.id)
                        .sort((a, b) =>
                          getTemplateDisplayName(a).localeCompare(
                            getTemplateDisplayName(b),
                          ),
                        )
                        .map((template) => ({
                          value: template.id!,
                          label: `${getTemplateDisplayName(template)} (${(template.ActivityMeasurementList || []).length} sets)`,
                        }))}
                      value=""
                      onChange={(value) => {
                        if (value === "") {
                          setSelectedTemplate(null);
                          onTemplateSelect?.(null);
                        } else {
                          const template = sessionTemplates.find(
                            (t) => t.id === value,
                          );
                          if (template) {
                            setSelectedTemplate(template);
                            onTemplateSelect?.(template);
                            onPlannedMeasurementsChange(
                              template.ActivityMeasurementList || [],
                            );

                            const templateActivityIds = Array.from(
                              new Set(
                                (template.ActivityMeasurementList || []).map(
                                  (measurement) => measurement.ActivityTypeID,
                                ),
                              ),
                            );
                            setRecentActivityIds((prev) => {
                              const combined = [
                                ...templateActivityIds,
                                ...prev,
                              ];
                              return Array.from(new Set(combined)).slice(0, 5);
                            });

                            setShowTemplateSelection(false);
                            setHasSelectedTemplate(true);
                            setError(null);
                          }
                        }
                      }}
                      placeholder={
                        sessionTemplates.length > 0
                          ? "Search session templates..."
                          : "No templates available"
                      }
                      allOptionLabel="Select a session template..."
                      emptyValue=""
                      disabled={sessionTemplates.length === 0}
                      inputClassName="bg-base-200"
                    />
                    <p className="text-center text-base-content/60">or</p>
                    {/* Start from Scratch Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTemplate(null);
                        onTemplateSelect?.(null);
                        onPlannedMeasurementsChange([]);
                        setShowTemplateSelection(false);
                        setHasSelectedTemplate(true);
                        setStartedFromScratch(true);
                      }}
                      className="w-full flex items-center justify-between gap-2 p-4 border border-base-300 bg-base-200 rounded-lg hover:border-base-content/40 hover:bg-base-300 transition-colors text-md font-medium text-base-content/80 font-grotesk"
                    >
                      Start from Scratch
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activities Section - Only show after template selection or scratch mode */}
          {(isLoggingMode ||
            selectedTemplate !== null ||
            hasSelectedTemplate) && (
            <div className="">
              {/* Clear button */}
              {!isLoggingMode && !existingSession && (
                <div className="flex justify-end mb-2 px-4 pb-4">
                  <button
                    type="button"
                    onClick={() => setShowClearConfirm(true)}
                    className="text-xs text-base-content/50 hover:text-error transition-colors font-grotesk"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Clear confirmation modal */}
              {showClearConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001]">
                  <div className="bg-base-100 rounded-xl shadow-xl p-6 mx-4 max-w-sm w-full space-y-4">
                    <h3 className="text-lg font-grotesk font-bold text-base-content">
                      Clear session details?
                    </h3>
                    <p className="text-sm text-base-content/60 font-grotesk">
                      This will remove all activities and take you back to the
                      starting step.
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowClearConfirm(false)}
                        className="flex-1 py-3 rounded-xl border border-base-300 text-base-content font-grotesk font-medium hover:bg-base-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTemplate(null);
                          onTemplateSelect?.(null);
                          onPlannedMeasurementsChange([]);
                          setHasSelectedTemplate(false);
                          setStartedFromScratch(false);
                          setShowTemplateSelection(false);
                          setError(null);
                          setShowClearConfirm(false);
                        }}
                        className="flex-1 py-3 rounded-xl bg-error text-white font-grotesk font-medium hover:bg-error/80 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {/* Planned Activities List */}
                <div>
                  {plannedMeasurements.length === 0 ? (
                    <div className="text-center py-8 text-base-content/60">
                      <p className="text-sm font-sans">
                        No exercises added yet
                      </p>
                      <p className="text-xs mt-1 font-sans">
                        Add activities below to build your session
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Collapse/Expand All Button */}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            const groupCount =
                              countActivityGroups(plannedMeasurements);
                            // If all are collapsed, expand all; otherwise collapse all
                            if (collapsedGroups.size >= groupCount) {
                              setCollapsedGroups(new Set());
                            } else {
                              setCollapsedGroups(
                                initialCollapsedGroups(plannedMeasurements),
                              );
                            }
                          }}
                          className="px-3 min-h-9 text-xs font-medium text-base-content/70 hover:text-base-content bg-base-100 border border-base-300 rounded-xl hover:bg-base-200 transition-colors font-grotesk flex items-center gap-1.5"
                        >
                          {collapsedGroups.size > 0 ? (
                            <>
                              <HiChevronDown className="w-3.5 h-3.5" />
                              Expand All
                            </>
                          ) : (
                            <>
                              <HiChevronRight className="w-3.5 h-3.5" />
                              Collapse All
                            </>
                          )}
                        </button>
                      </div>
                      {(() => {
                        // Group consecutive measurements with the same ActivityTypeID (and globalCategoryID for pack activities)
                        type ActivityGroup = {
                          activityTypeId: string;
                          globalCategoryID?: string;
                          supersetGroupId?: string;
                          activityName: string;
                          measurements: {
                            measurement: ActivityMeasurement;
                            originalIndex: number;
                          }[];
                        };
                        const groups: ActivityGroup[] = [];

                        plannedMeasurements.forEach(
                          (measurement, measurementIndex) => {
                            const lastGroup = groups[groups.length - 1];
                            // Create a unique key that includes globalCategoryID
                            const groupKey = measurement.globalCategoryID
                              ? `${measurement.ActivityTypeID}_${measurement.globalCategoryID}`
                              : measurement.ActivityTypeID;
                            const lastGroupKey = lastGroup
                              ? lastGroup.globalCategoryID
                                ? `${lastGroup.activityTypeId}_${lastGroup.globalCategoryID}`
                                : lastGroup.activityTypeId
                              : null;

                            if (lastGroup && lastGroupKey === groupKey) {
                              // Add to existing group
                              lastGroup.measurements.push({
                                measurement,
                                originalIndex: measurementIndex,
                              });
                            } else {
                              // Start new group
                              groups.push({
                                activityTypeId: measurement.ActivityTypeID,
                                globalCategoryID: measurement.globalCategoryID,
                                supersetGroupId: measurement.supersetGroupId,
                                activityName: getActivityName(measurement),
                                measurements: [
                                  {
                                    measurement,
                                    originalIndex: measurementIndex,
                                  },
                                ],
                              });
                            }
                          },
                        );

                        // Build superset blocks: consecutive groups sharing the same supersetGroupId are merged
                        type SupersetBlock = {
                          supersetGroupId?: string;
                          groups: ActivityGroup[];
                        };
                        const blocks: SupersetBlock[] = [];
                        groups.forEach((group) => {
                          const lastBlock = blocks[blocks.length - 1];
                          if (
                            group.supersetGroupId &&
                            lastBlock?.supersetGroupId === group.supersetGroupId
                          ) {
                            lastBlock.groups.push(group);
                          } else {
                            blocks.push({
                              supersetGroupId: group.supersetGroupId,
                              groups: [group],
                            });
                          }
                        });

                        // Track global group index across blocks for collapse state etc.
                        let globalGroupIndex = 0;

                        return blocks.map((block, blockIndex) => {
                          const isSuperset =
                            !!block.supersetGroupId && block.groups.length > 1;
                          const blockContent = block.groups.map((group) => {
                            const groupIndex = globalGroupIndex++;
                            const variables =
                              group.measurements[0].measurement.variables;

                            return (
                              <div
                                key={groupIndex}
                                className=""
                                data-group-index={groupIndex}
                              >
                                {/* Group header: name, set count, chevron — tap to collapse */}
                                <div className="flex items-center justify-between gap-2 mb-3 pt-3 border-t border-base-300">
                                  <button
                                    type="button"
                                    aria-expanded={
                                      !collapsedGroups.has(groupIndex)
                                    }
                                    onClick={() => {
                                      setCollapsedGroups((prev) => {
                                        const newSet = new Set(prev);
                                        if (newSet.has(groupIndex)) {
                                          newSet.delete(groupIndex);
                                        } else {
                                          newSet.add(groupIndex);
                                        }
                                        return newSet;
                                      });
                                    }}
                                    className="flex flex-1 min-w-0 min-h-11 items-center gap-2 rounded-xl px-2 py-2 -ml-2 text-left hover:bg-base-200 transition-colors cursor-pointer"
                                  >
                                    <HiChevronDown
                                      className={`w-4 h-4 flex-shrink-0 text-base-content/60 transition-transform duration-200 ${
                                        collapsedGroups.has(groupIndex)
                                          ? "-rotate-90"
                                          : ""
                                      }`}
                                    />
                                    <span className="truncate font-grotesk font-semibold text-base text-base-content">
                                      {group.activityName}
                                    </span>
                                    <span className="flex-shrink-0 rounded-full bg-base-200 px-2 py-0.5 text-xs font-geist tabular-nums text-base-content/70">
                                      {group.measurements.length}{" "}
                                      {group.measurements.length === 1
                                        ? "set"
                                        : "sets"}
                                    </span>
                                  </button>
                                  {/* 3-dot menu dropdown */}
                                  <Menu as="div" className="relative">
                                    <Menu.Button
                                      disabled={loading || readOnly}
                                      className="flex h-11 w-11 items-center justify-center text-base-content/50 hover:bg-base-200 rounded-full transition-colors disabled:opacity-50"
                                      aria-label="Activity options"
                                    >
                                      <HiEllipsisVertical className="w-5 h-5" />
                                    </Menu.Button>
                                    <Transition
                                      as={Fragment}
                                      enter="transition ease-out duration-100"
                                      enterFrom="transform opacity-0 scale-95"
                                      enterTo="transform opacity-100 scale-100"
                                      leave="transition ease-in duration-75"
                                      leaveFrom="transform opacity-100 scale-100"
                                      leaveTo="transform opacity-0 scale-95"
                                    >
                                      <Menu.Items className="absolute right-0 mt-2 w-52 origin-top-right rounded-xl bg-base-100 shadow-lg border border-base-300 focus:outline-none z-50 overflow-hidden">
                                        <div className="py-1">
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() => {
                                                  setHiddenActionGroups(
                                                    (prev) => {
                                                      const newSet = new Set(
                                                        prev,
                                                      );
                                                      if (
                                                        newSet.has(groupIndex)
                                                      ) {
                                                        newSet.delete(
                                                          groupIndex,
                                                        );
                                                      } else {
                                                        newSet.add(groupIndex);
                                                      }
                                                      return newSet;
                                                    },
                                                  );
                                                }}
                                                className={`${
                                                  active ? "bg-base-200" : ""
                                                } group flex w-full items-center gap-2 px-4 py-2 text-sm text-base-content/80`}
                                              >
                                                {hiddenActionGroups.has(
                                                  groupIndex,
                                                ) ? (
                                                  <>
                                                    <HiPencil className="w-4 h-4" />
                                                    Show Actions
                                                  </>
                                                ) : (
                                                  <>
                                                    <HiEyeSlash className="w-4 h-4" />
                                                    Hide Actions
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </Menu.Item>
                                          {/* Restore All Columns - only show if any columns are hidden for this group */}
                                          {variables.some((_, idx) =>
                                            hiddenColumns.has(
                                              `${groupIndex}-${idx}`,
                                            ),
                                          ) && (
                                            <Menu.Item>
                                              {({ active }) => (
                                                <button
                                                  onClick={() => {
                                                    // Remove all hidden columns for this group
                                                    const newHiddenColumns =
                                                      new Set(hiddenColumns);
                                                    variables.forEach(
                                                      (_, idx) => {
                                                        newHiddenColumns.delete(
                                                          `${groupIndex}-${idx}`,
                                                        );
                                                      },
                                                    );
                                                    setHiddenColumns(
                                                      newHiddenColumns,
                                                    );
                                                  }}
                                                  className={`${
                                                    active ? "bg-base-200" : ""
                                                  } group flex w-full items-center gap-2 px-4 py-2 text-sm text-base-content/80`}
                                                >
                                                  <HiArrowPath className="w-4 h-4" />
                                                  Restore All Columns
                                                </button>
                                              )}
                                            </Menu.Item>
                                          )}
                                          {/* Superset actions */}
                                          {!group.supersetGroupId &&
                                            (() => {
                                              // Find the index of this group in the full groups array
                                              const thisGroupIdx =
                                                groups.indexOf(group);
                                              const nextGroup =
                                                thisGroupIdx < groups.length - 1
                                                  ? groups[thisGroupIdx + 1]
                                                  : null;
                                              const prevGroup =
                                                thisGroupIdx > 0
                                                  ? groups[thisGroupIdx - 1]
                                                  : null;
                                              return (
                                                <>
                                                  {nextGroup &&
                                                    !nextGroup.supersetGroupId && (
                                                      <Menu.Item>
                                                        {({ active }) => (
                                                          <button
                                                            onClick={() => {
                                                              handleCreateSuperset(
                                                                [
                                                                  group.measurements.map(
                                                                    (m) =>
                                                                      m.originalIndex,
                                                                  ),
                                                                  nextGroup.measurements.map(
                                                                    (m) =>
                                                                      m.originalIndex,
                                                                  ),
                                                                ],
                                                              );
                                                            }}
                                                            disabled={readOnly}
                                                            className={`${active ? "bg-base-200" : ""} group flex w-full items-center gap-2 px-4 py-2 text-sm text-base-content/80`}
                                                          >
                                                            ⚡ Superset with
                                                            Next
                                                          </button>
                                                        )}
                                                      </Menu.Item>
                                                    )}
                                                  {prevGroup?.supersetGroupId && (
                                                    <Menu.Item>
                                                      {({ active }) => (
                                                        <button
                                                          onClick={() => {
                                                            // Add this group to the previous group's superset
                                                            const supersetId =
                                                              prevGroup.supersetGroupId!;
                                                            const indices =
                                                              group.measurements.map(
                                                                (m) =>
                                                                  m.originalIndex,
                                                              );
                                                            const updatedMeasurements =
                                                              plannedMeasurements.map(
                                                                (m, i) =>
                                                                  indices.includes(
                                                                    i,
                                                                  )
                                                                    ? {
                                                                        ...m,
                                                                        supersetGroupId:
                                                                          supersetId,
                                                                      }
                                                                    : m,
                                                              );
                                                            onPlannedMeasurementsChange(
                                                              updatedMeasurements,
                                                            );
                                                          }}
                                                          disabled={readOnly}
                                                          className={`${active ? "bg-base-200" : ""} group flex w-full items-center gap-2 px-4 py-2 text-sm text-base-content/80`}
                                                        >
                                                          ⚡ Add to Superset
                                                          Above
                                                        </button>
                                                      )}
                                                    </Menu.Item>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          <Menu.Item>
                                            {({ active }) => (
                                              <button
                                                onClick={() => {
                                                  // Remove all measurements in the group at once
                                                  const indicesToRemove =
                                                    new Set(
                                                      group.measurements.map(
                                                        (m) => m.originalIndex,
                                                      ),
                                                    );
                                                  if (onRemoveGroup) {
                                                    onRemoveGroup(
                                                      Array.from(
                                                        indicesToRemove,
                                                      ),
                                                    );
                                                  } else {
                                                    const updatedMeasurements =
                                                      plannedMeasurements.filter(
                                                        (_, index) =>
                                                          !indicesToRemove.has(
                                                            index,
                                                          ),
                                                      );
                                                    onPlannedMeasurementsChange(
                                                      updatedMeasurements,
                                                    );
                                                  }
                                                }}
                                                disabled={readOnly}
                                                className={`${
                                                  active ? "bg-base-200" : ""
                                                } group flex w-full items-center gap-2 px-4 py-2.5 min-h-11 text-sm text-error font-grotesk disabled:opacity-50`}
                                              >
                                                <HiTrash className="w-4 h-4" />
                                                Remove All Sets
                                              </button>
                                            )}
                                          </Menu.Item>
                                        </div>
                                      </Menu.Items>
                                    </Transition>
                                  </Menu>
                                </div>

                                {/* Collapsible content wrapper */}
                                <div
                                  className={`transition-all duration-200 ease-in-out overflow-hidden ${
                                    collapsedGroups.has(groupIndex)
                                      ? "max-h-0 opacity-0"
                                      : "max-h-[5000px] opacity-100"
                                  }`}
                                >
                                  {group.measurements[0]?.measurement.note && (
                                    <div className="mb-3 px-3 py-2 rounded-xl bg-base-200">
                                      <p className="text-sm text-base-content/80 font-grotesk">
                                        Note:{" "}
                                        {group.measurements[0].measurement.note}
                                      </p>
                                    </div>
                                  )}

                                  {/* Unified scrollable container for headers + all measurement rows */}
                                  {variables.length > 0 && (
                                    <div
                                      className="overflow-x-auto sm:scrollbar-hide"
                                      style={{
                                        WebkitOverflowScrolling: "touch",
                                        scrollbarWidth: "none", // Firefox - hide on desktop
                                        msOverflowStyle: "none", // IE/Edge
                                      }}
                                    >
                                      {/* Inner container – on desktop use min-w-max so it expands to fit Actions column */}
                                      <div className="inline-block min-w-full sm:min-w-max">
                                        {/* Variable headers */}
                                        <div className="mb-3 relative z-20">
                                          <div className="flex gap-1 sm:gap-3 items-center">
                                            {/* Variable header columns */}
                                            <div className="flex-1 min-w-0">
                                              <div className="flex gap-1 sm:gap-3">
                                                {variables.map(
                                                  (variable, variableIndex) => {
                                                    const columnKey = `${groupIndex}-${variableIndex}`;
                                                    const isHidden =
                                                      hiddenColumns.has(
                                                        columnKey,
                                                      );
                                                    const currentUnit =
                                                      variable.unit;
                                                    const availableUnits =
                                                      getUnitsForVariable(
                                                        variable.name,
                                                      );

                                                    if (isHidden) return null;

                                                    // Check if this column is animating out
                                                    const isHiding =
                                                      hidingColumns.has(
                                                        columnKey,
                                                      );

                                                    return (
                                                      <div
                                                        key={variableIndex}
                                                        className={`${VARIABLE_COLUMN_WIDTH_CLASS} flex-shrink-0 transition-all duration-300 ${
                                                          isHiding
                                                            ? "opacity-0 scale-x-0 w-0 sm:w-0 overflow-hidden"
                                                            : "opacity-100 scale-x-100"
                                                        }`}
                                                      >
                                                        <div className="flex items-baseline px-2 sm:px-3 gap-1 min-w-0">
                                                          <button
                                                            onClick={() => {
                                                              if (isHidden) {
                                                                // Show column immediately
                                                                const newHiddenColumns =
                                                                  new Set(
                                                                    hiddenColumns,
                                                                  );
                                                                newHiddenColumns.delete(
                                                                  columnKey,
                                                                );
                                                                setHiddenColumns(
                                                                  newHiddenColumns,
                                                                );
                                                              } else {
                                                                // Start hide animation
                                                                const newHidingColumns =
                                                                  new Set(
                                                                    hidingColumns,
                                                                  );
                                                                newHidingColumns.add(
                                                                  columnKey,
                                                                );
                                                                setHidingColumns(
                                                                  newHidingColumns,
                                                                );

                                                                // After animation completes, actually hide the column
                                                                setTimeout(
                                                                  () => {
                                                                    setHiddenColumns(
                                                                      (
                                                                        prev,
                                                                      ) => {
                                                                        const next =
                                                                          new Set(
                                                                            prev,
                                                                          );
                                                                        next.add(
                                                                          columnKey,
                                                                        );
                                                                        return next;
                                                                      },
                                                                    );
                                                                    setHidingColumns(
                                                                      (
                                                                        prev,
                                                                      ) => {
                                                                        const next =
                                                                          new Set(
                                                                            prev,
                                                                          );
                                                                        next.delete(
                                                                          columnKey,
                                                                        );
                                                                        return next;
                                                                      },
                                                                    );
                                                                  },
                                                                  300,
                                                                ); // Match transition duration
                                                              }
                                                            }}
                                                            className="min-w-0 truncate text-xs sm:text-sm font-grotesk font-medium text-base-content/70 whitespace-nowrap text-left cursor-pointer hover:bg-base-200 rounded transition-colors"
                                                            title="Click to hide column"
                                                          >
                                                            {variable.name}
                                                          </button>
                                                          <div className="relative">
                                                            <input
                                                              type="text"
                                                              value={
                                                                currentUnit ||
                                                                ""
                                                              }
                                                              onChange={(e) => {
                                                                handleGroupUnitChange(
                                                                  group.measurements.map(
                                                                    (m) =>
                                                                      m.originalIndex,
                                                                  ),
                                                                  variableIndex,
                                                                  e.target
                                                                    .value,
                                                                );
                                                              }}
                                                              onFocus={(e) => {
                                                                const rect =
                                                                  e.currentTarget.getBoundingClientRect();
                                                                setUnitDropdownRect(
                                                                  {
                                                                    top: rect.bottom,
                                                                    left: rect.left,
                                                                    width:
                                                                      rect.width,
                                                                  },
                                                                );
                                                                setActiveUnitDropdown(
                                                                  `header-${groupIndex}-${variableIndex}`,
                                                                );
                                                              }}
                                                              onBlur={() => {
                                                                setTimeout(
                                                                  () => {
                                                                    setActiveUnitDropdown(
                                                                      null,
                                                                    );
                                                                    setUnitDropdownRect(
                                                                      null,
                                                                    );
                                                                  },
                                                                  200,
                                                                );
                                                              }}
                                                              placeholder="unit"
                                                              disabled={loading}
                                                              className="w-10 text-[16px] sm:text-[10px] px-1 py-0.5 border border-base-300 rounded focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-300 bg-base-100 disabled:opacity-50 disabled:cursor-not-allowed font-grotesk text-center"
                                                            />
                                                            {activeUnitDropdown ===
                                                              `header-${groupIndex}-${variableIndex}` &&
                                                              availableUnits.length >
                                                                0 &&
                                                              unitDropdownRect &&
                                                              ReactDOM.createPortal(
                                                                <div
                                                                  className="fixed z-[99999] w-32 bg-base-100 border border-base-300 rounded-xl shadow-lg max-h-40 overflow-y-auto"
                                                                  style={{
                                                                    top:
                                                                      unitDropdownRect.top +
                                                                      4,
                                                                    left: unitDropdownRect.left,
                                                                  }}
                                                                  onMouseDown={(
                                                                    e,
                                                                  ) =>
                                                                    e.preventDefault()
                                                                  }
                                                                >
                                                                  {availableUnits.map(
                                                                    (unit) => (
                                                                      <button
                                                                        key={
                                                                          unit
                                                                        }
                                                                        type="button"
                                                                        onClick={() => {
                                                                          handleGroupUnitChange(
                                                                            group.measurements.map(
                                                                              (
                                                                                m,
                                                                              ) =>
                                                                                m.originalIndex,
                                                                            ),
                                                                            variableIndex,
                                                                            unit,
                                                                          );
                                                                          setActiveUnitDropdown(
                                                                            null,
                                                                          );
                                                                          setUnitDropdownRect(
                                                                            null,
                                                                          );
                                                                        }}
                                                                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-base-200 focus:bg-base-200 focus:outline-none font-grotesk"
                                                                      >
                                                                        {unit}
                                                                      </button>
                                                                    ),
                                                                  )}
                                                                </div>,
                                                                document.body,
                                                              )}
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  },
                                                )}
                                              </div>
                                            </div>
                                            {/* Actions column header - show by default, hide when toggled */}
                                            {!hiddenActionGroups.has(
                                              groupIndex,
                                            ) && (
                                              <div className="flex-shrink-0 w-20 sm:w-24 text-center">
                                                <span className="text-xs sm:text-sm font-grotesk font-medium text-base-content/60">
                                                  Actions
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Measurement rows */}
                                        <div className="space-y-3 relative z-10">
                                          {group.measurements.map(
                                            ({
                                              measurement,
                                              originalIndex,
                                            }) => {
                                              const isLogged =
                                                !!measurement.timeLogged;
                                              // A set is visually complete when every visible variable
                                              // has a value. We no longer require `timeLogged` here so
                                              // historical sessions (saved before per-set auto-logging
                                              // respected hidden columns) still render as completed.
                                              const allVisibleFilled =
                                                measurement.variables
                                                  .filter((v) => !v.hidden)
                                                  .every(
                                                    (v) =>
                                                      v.measurement !== null &&
                                                      v.measurement !==
                                                        undefined,
                                                  );
                                              const sessionStatus =
                                                existingSession &&
                                                "status" in existingSession
                                                  ? existingSession.status
                                                  : undefined;
                                              const isComplete =
                                                allVisibleFilled &&
                                                (isLogged ||
                                                  sessionStatus === "complete" ||
                                                  sessionStatus === "executing");

                                              return (
                                                <div
                                                  key={originalIndex}
                                                  className={`transition-all duration-500 ${
                                                    deletingIndex ===
                                                    originalIndex
                                                      ? "opacity-0 scale-95 -translate-x-4"
                                                      : newlyAddedIndex ===
                                                          originalIndex
                                                        ? "opacity-100 scale-100 translate-x-0 animate-slide-in"
                                                        : "opacity-100 scale-100 translate-x-0"
                                                  }`}
                                                >
                                                  <div className="flex gap-1 sm:gap-3 w-full items-center">
                                                    {/* Variable inputs */}
                                                    <div className="flex-1 min-w-0">
                                                      <div className="flex gap-1 sm:gap-3">
                                                        {measurement.variables.map(
                                                          (
                                                            variable,
                                                            variableIndex,
                                                          ) => {
                                                            const columnKey = `${groupIndex}-${variableIndex}`;
                                                            const isHidden =
                                                              hiddenColumns.has(
                                                                columnKey,
                                                              );
                                                            const isHiding =
                                                              hidingColumns.has(
                                                                columnKey,
                                                              );

                                                            if (isHidden)
                                                              return null;

                                                            const isTimeVar =
                                                              isTimeDurationVariable(
                                                                variable.name,
                                                              );

                                                            // Three visual cell states (plan 3f): empty (no value yet),
                                                            // filled-but-set-incomplete, and complete (every visible
                                                            // value present). DaisyUI tokens only.
                                                            const hasValue =
                                                              variable.measurement !==
                                                                null &&
                                                              variable.measurement !==
                                                                undefined;
                                                            const cellStateClassName =
                                                              isComplete
                                                                ? "bg-success/15 border-success/60 text-base-content"
                                                                : hasValue
                                                                  ? "bg-base-100 border-base-content/30 text-base-content"
                                                                  : "bg-base-200 border-base-300 text-base-content/80 placeholder:text-base-content/30";
                                                            const cellInputClassName = `w-full input text-[16px] sm:text-sm font-geist font-semibold tabular-nums focus:outline-none focus:ring-1 focus:ring-base-content/80 focus:border-base-content/80 border-1 border p-2 sm:p-3 min-h-11 rounded-xl transition-colors ${cellStateClassName}`;

                                                            return (
                                                              <div
                                                                key={
                                                                  variableIndex
                                                                }
                                                                className={`${VARIABLE_COLUMN_WIDTH_CLASS} flex-shrink-0 transition-all duration-300 ${
                                                                  isHiding
                                                                    ? "opacity-0 scale-x-0 w-0 sm:w-0 overflow-hidden"
                                                                    : "opacity-100 scale-x-100"
                                                                }`}
                                                              >
                                                                {isTimeVar ? (
                                                                  <TimeDurationInput
                                                                    measurement={
                                                                      variable.measurement
                                                                    }
                                                                    unit={
                                                                      variable.unit
                                                                    }
                                                                    disabled={
                                                                      loading ||
                                                                      readOnly
                                                                    }
                                                                    onChange={(
                                                                      value,
                                                                      unit,
                                                                    ) =>
                                                                      handleUpdateVariable(
                                                                        originalIndex,
                                                                        variableIndex,
                                                                        value,
                                                                        unit,
                                                                      )
                                                                    }
                                                                    className={
                                                                      cellInputClassName
                                                                    }
                                                                  />
                                                                ) : (
                                                                  <input
                                                                    type="number"
                                                                    value={
                                                                      variable.measurement ??
                                                                      ""
                                                                    }
                                                                    onChange={(
                                                                      e,
                                                                    ) => {
                                                                      const value =
                                                                        e
                                                                          .target
                                                                          .value ===
                                                                        ""
                                                                          ? null
                                                                          : parseFloat(
                                                                              e
                                                                                .target
                                                                                .value,
                                                                            );
                                                                      handleUpdateVariable(
                                                                        originalIndex,
                                                                        variableIndex,
                                                                        value,
                                                                      );
                                                                    }}
                                                    placeholder="–"
                                                    className={
                                                      cellInputClassName
                                                    }
                                                                    style={{
                                                                      MozAppearance:
                                                                        "textfield",
                                                                      WebkitAppearance:
                                                                        "none",
                                                                      margin: 0,
                                                                    }}
                                                                    onWheel={(
                                                                      e,
                                                                    ) =>
                                                                      e.currentTarget.blur()
                                                                    }
                                                                    disabled={
                                                                      loading ||
                                                                      readOnly
                                                                    }
                                                                    title=""
                                                                  />
                                                                )}
                                                              </div>
                                                            );
                                                          },
                                                        )}
                                                      </div>
                                                    </div>
                                                    {/* Actions column - show by default, hide when toggled */}
                                                    {!hiddenActionGroups.has(
                                                      groupIndex,
                                                    ) && (
                                                      <div className="flex-shrink-0 w-20 sm:w-24 flex items-center justify-center gap-1">
                                                        <button
                                                          onClick={() =>
                                                            handleDuplicateActivity(
                                                              originalIndex,
                                                            )
                                                          }
                                                          disabled={
                                                            loading || readOnly
                                                          }
                                                          className="p-2 text-base-content/50 hover:bg-base-100 rounded-full transition-colors disabled:opacity-50"
                                                          title="Duplicate this set"
                                                        >
                                                          <HiDocumentDuplicate className="w-4 h-4" />
                                                        </button>
                                                        {group.measurements
                                                          .length > 1 && (
                                                          <button
                                                            onClick={() =>
                                                              handleRemoveActivity(
                                                                originalIndex,
                                                              )
                                                            }
                                                            disabled={
                                                              loading ||
                                                              readOnly
                                                            }
                                                            className="p-2 text-base-content/50 hover:bg-base-100 rounded-full transition-colors disabled:opacity-50"
                                                            title="Remove this set"
                                                          >
                                                            <HiTrash className="w-4 h-4" />
                                                          </button>
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Outside the overflow-hidden collapse clip so both actions stay visible. */}
                                {!collapsedGroups.has(groupIndex) &&
                                  !bulkSelectionMode && (
                                    <div className="mt-3 pb-5 flex flex-col gap-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const lastMeasurementIndex = Math.max(
                                            ...group.measurements.map(
                                              (m) => m.originalIndex,
                                            ),
                                          );
                                          handleRecentActivityAdd(
                                            group.activityTypeId,
                                            lastMeasurementIndex,
                                            group.globalCategoryID,
                                          );
                                        }}
                                        className="w-full min-h-11 px-3 py-2 text-sm text-base-content/60 hover:text-base-content bg-base-100 border border-dashed border-base-300 rounded-xl hover:bg-base-200 transition-colors font-grotesk"
                                        disabled={loading || readOnly}
                                      >
                                        + Add Set
                                      </button>
                                      {allowActivitySelection && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const lastMeasurementIndex =
                                              Math.max(
                                                ...group.measurements.map(
                                                  (m) => m.originalIndex,
                                                ),
                                              );
                                            openInsertActivity(
                                              lastMeasurementIndex,
                                            );
                                          }}
                                          className="w-full px-3 py-2 text-sm font-medium text-base-content/70 border border-dashed border-base-content/25 rounded-md bg-base-content/10 hover:bg-base-content/15 hover:text-base-content transition-colors font-sans"
                                          disabled={loading || readOnly}
                                        >
                                          + Add Exercise
                                        </button>
                                      )}
                                    </div>
                                  )}
                              </div>
                            );
                          });

                          // Wrap in superset container or render flat
                          if (isSuperset) {
                            return (
                              <div
                                key={`superset-${block.supersetGroupId}`}
                                className="border-l-4 border-base-content/30 rounded-lg bg-base-content/5 pl-2 py-2 my-2"
                              >
                                <div className="flex items-center justify-between px-2 pb-2">
                                  <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60 font-grotesk">
                                    ⚡ Superset
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleBreakSuperset(
                                        block.supersetGroupId!,
                                      )
                                    }
                                    disabled={readOnly}
                                    className="text-xs text-base-content/50 hover:text-error transition-colors disabled:opacity-50 font-grotesk min-h-9 px-1"
                                  >
                                    Break Superset
                                  </button>
                                </div>
                                {blockContent}
                              </div>
                            );
                          }
                          return (
                            <React.Fragment key={`block-${blockIndex}`}>
                              {blockContent}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-error/10 border border-error/30 rounded-xl p-3" role="alert">
                  <p className="text-sm text-error font-grotesk">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI dictation Floating Button - sits to the LEFT of the + Add
            Activity FAB on desktop, and ABOVE it on mobile, so both stay
            tappable without overlap. */}
        {!hideFloatingAIButton &&
          (isLoggingMode || selectedTemplate !== null || hasSelectedTemplate) &&
          !readOnly && (
            <div className="fixed md:absolute right-6 bottom-40 md:right-[64px] md:bottom-[18px] z-[9999] md:z-10">
              <AILogButton
                size="sm"
                disablePulse
                prompt={aiPrompt}
                logFunctions={aiLogFunctions}
                title="Dictate session changes with AI"
              />
            </div>
          )}

        {/* Add Activity Floating Button */}
        {(isLoggingMode || selectedTemplate !== null || hasSelectedTemplate) &&
          allowActivitySelection &&
          !readOnly && (
            <div className="px-8 pb-32">
              <ActivitySelector
                isFloatingButton={floatingActivityButton}
                openRequestId={activitySelectorOpenId}
                onClosed={() => {
                  insertActivityAfterRef.current = undefined;
                }}
                onAdd={(activityType) => {
                  const insertAfter = insertActivityAfterRef.current;
                  insertActivityAfterRef.current = undefined;
                  if (onAddActivity) {
                    onAddActivity(
                      activityType,
                      insertAfter !== undefined
                        ? insertAfter + indexOffset
                        : undefined,
                    );
                    return;
                  }
                  // Create ActivityMeasurement with variables initialized from activityType.variables
                  const normalizedVars = normalizeActivityVariables(
                    activityType.variables,
                  );

                  // Check if user prefers imperial units
                  const isImperial = userData?.isImperial || false;

                  // Debug logging
                  console.log(
                    "🎯 ActivitySelector onAdd - Activity Type:",
                    activityType.name,
                  );
                  console.log("🎯 Raw variables:", activityType.variables);
                  console.log("🎯 Normalized variables:", normalizedVars);

                  const variables: ActivityVariable[] = normalizedVars.map(
                    (variable, index) => {
                      const unit =
                        variable.defaultUnit ||
                        getDefaultUnit(variable.name, isImperial);
                      console.log(`🎯 Variable ${index} (${variable.name}):`, {
                        defaultUnit: variable.defaultUnit,
                        calculatedUnit: unit,
                      });
                      return {
                        name: variable.name,
                        measurement: null,
                        // Use the activity's defined defaultUnit if it exists, otherwise use system default
                        unit: unit,
                      };
                    },
                  );

                  console.log("🎯 Final variables array:", variables);

                  // For pack activities, extract the real ID and set globalCategoryID
                  let activityTypeId = activityType.id!;
                  let globalCategoryID: string | undefined;

                  if (activityType.isFromPack && activityType.categoryId) {
                    // ID format is `global_${categoryId}_${realId}` - extract the real ID
                    const parts = activityTypeId.split("_");
                    if (parts.length >= 3) {
                      activityTypeId = parts.slice(2).join("_"); // Get everything after `global_${categoryId}_`
                    }
                    globalCategoryID = activityType.categoryId;
                  }

                  const newMeasurement: ActivityMeasurement = {
                    ActivityTypeID: activityTypeId,
                    ...(activityType.name && {
                      activityName: activityType.name,
                    }),
                    variables: variables,
                    ...(globalCategoryID && { globalCategoryID }),
                  };

                  onPlannedMeasurementsChange(
                    insertItemsAfter(
                      plannedMeasurements,
                      [newMeasurement],
                      insertAfter,
                    ),
                  );
                  upsertUserActivityTypes([activityType]);
                  setLastSelectedActivityId(activityType.id!);
                  setError(null);
                }}
                onBulkAdd={(activityTypes) => {
                  const insertAfter = insertActivityAfterRef.current;
                  insertActivityAfterRef.current = undefined;
                  if (onBulkAddActivities) {
                    onBulkAddActivities(
                      activityTypes,
                      insertAfter !== undefined
                        ? insertAfter + indexOffset
                        : undefined,
                    );
                    return;
                  }
                  // Check if user prefers imperial units
                  const isImperial = userData?.isImperial || false;

                  console.log(
                    "🎯🎯 BULK ADD - Adding",
                    activityTypes.length,
                    "activities",
                  );

                  const newMeasurements: ActivityMeasurement[] =
                    activityTypes.map((activityType) => {
                      console.log(
                        "🎯🎯 Processing activity:",
                        activityType.name,
                      );
                      console.log(
                        "🎯🎯 Raw variables:",
                        activityType.variables,
                      );

                      const normalizedVars = normalizeActivityVariables(
                        activityType.variables,
                      );

                      console.log("🎯🎯 Normalized variables:", normalizedVars);

                      const variables: ActivityVariable[] = normalizedVars.map(
                        (variable, index) => {
                          const unit =
                            variable.defaultUnit ||
                            getDefaultUnit(variable.name, isImperial);
                          console.log(
                            `🎯🎯 Variable ${index} (${variable.name}):`,
                            {
                              defaultUnit: variable.defaultUnit,
                              calculatedUnit: unit,
                            },
                          );
                          return {
                            name: variable.name,
                            measurement: null,
                            // Use the activity's defined defaultUnit if it exists, otherwise use system default
                            unit: unit,
                          };
                        },
                      );

                      console.log(
                        "🎯🎯 Final variables for",
                        activityType.name,
                        ":",
                        variables,
                      );

                      // For pack activities, extract the real ID and set globalCategoryID
                      let activityTypeId = activityType.id!;
                      let globalCategoryID: string | undefined;

                      if (activityType.isFromPack && activityType.categoryId) {
                        // ID format is `global_${categoryId}_${realId}` - extract the real ID
                        const parts = activityTypeId.split("_");
                        if (parts.length >= 3) {
                          activityTypeId = parts.slice(2).join("_");
                        }
                        globalCategoryID = activityType.categoryId;
                      }

                      return {
                        ActivityTypeID: activityTypeId,
                        ...(activityType.name && {
                          activityName: activityType.name,
                        }),
                        variables: variables,
                        ...(globalCategoryID && { globalCategoryID }),
                      };
                    });

                  onPlannedMeasurementsChange(
                    insertItemsAfter(
                      plannedMeasurements,
                      newMeasurements,
                      insertAfter,
                    ),
                  );
                  upsertUserActivityTypes(activityTypes);
                  setError(null);
                }}
                disabled={loading}
                showBulkSelect={true}
                allowCreate={true}
                title="Add Exercise"
                lastSelectedActivityId={lastSelectedActivityId}
              />

              {/* Alternative to building from scratch: load a session template */}
              {startInBuildMode &&
                !hideTemplatePicker &&
                !isLoggingMode &&
                !existingSession && (
                <div className="mt-4">
                  <p className="mb-2 text-[11px] font-medium uppercase tracking-wider text-base-content/50 font-grotesk">
                    Or start from a session template
                  </p>
                  <SearchableSelect
                    options={sessionTemplates
                      .filter((t) => !!t.id)
                      .sort((a, b) =>
                        getTemplateDisplayName(a).localeCompare(
                          getTemplateDisplayName(b),
                        ),
                      )
                      .map((template) => ({
                        value: template.id!,
                        label: `${getTemplateDisplayName(template)} (${(template.ActivityMeasurementList || []).length} sets)`,
                      }))}
                    value={selectedTemplate?.id || ""}
                    onChange={(value) => {
                      if (value === "") {
                        setSelectedTemplate(null);
                        onTemplateSelect?.(null);
                        return;
                      }
                      const template = sessionTemplates.find(
                        (t) => t.id === value,
                      );
                      if (template) {
                        setSelectedTemplate(template);
                        onTemplateSelect?.(template);
                        onPlannedMeasurementsChange(
                          template.ActivityMeasurementList || [],
                        );
                        const templateActivityIds = Array.from(
                          new Set(
                            (template.ActivityMeasurementList || []).map(
                              (measurement) => measurement.ActivityTypeID,
                            ),
                          ),
                        );
                        setRecentActivityIds((prev) =>
                          Array.from(
                            new Set([...templateActivityIds, ...prev]),
                          ).slice(0, 5),
                        );
                        setHasSelectedTemplate(true);
                        setStartedFromScratch(true);
                        setError(null);
                      }
                    }}
                    placeholder={
                      sessionTemplates.length > 0
                        ? "Search session templates..."
                        : "No templates available"
                    }
                    allOptionLabel="Select a session template..."
                    emptyValue=""
                    disabled={sessionTemplates.length === 0}
                    inputClassName="bg-base-200"
                  />
                </div>
              )}
            </div>
          )}

        {/* END COMPONENT */}
      </div>

      {/* Create Activity Type Dialog */}
      <CreateActivityTypeDialog
        isOpen={isCreateActivityDialogOpen}
        onClose={() => setIsCreateActivityDialogOpen(false)}
        initialName={searchTerm}
        onActivityTypeCreated={handleActivityTypeCreated}
      />
    </>
  );
};

/** Insert `items` after `afterIndex`, or append when `afterIndex` is omitted. */
function insertItemsAfter<T>(list: T[], items: T[], afterIndex?: number): T[] {
  if (afterIndex === undefined) return [...list, ...items];
  const next = [...list];
  next.splice(afterIndex + 1, 0, ...items);
  return next;
}

export default MasterSessionDetails;
